import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Link, SxProps, Theme, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import cloneDeep from 'lodash/cloneDeep';
import { useSnackbar } from 'notistack';

import { client, taskFormsApi } from '../../axios';
import { useLanguage } from '../../hooks';
import { useChangeLogger } from '../../hooks/useChangeLogger';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useProjectConfig } from '../../hooks/useProjectConfig';
import { useAppDispatch } from '../../store/hooks';
import { updateCurrentTaskFormId } from '../../store/modules/currentTaskForm.slice';
import { setIsActive } from '../../store/modules/listManager.slice';
import { theme } from '../../style/theme';
import { NestableLocations, QuestionUnsafe, SkipLogicQuestion } from '../../types/formBuilder';
import { QuestionInterface } from '../../types/oldWebForms';
import {
  MetadataAlternativeCommentsQuestion,
  MetadataNumberQuestion,
  MetadataOptionsQuestion,
  MeterConfiguration,
  TextQuestion,
} from '../../types/questions';
import { datascopeTracker } from '../../utils/eventTracker';
import ListInformation from '../ListManager/ListInformation/ListInformation';
import { convertSelectedLocationsToObject } from '../Locations/utils/LocationsHelpers';
import { SelectedStatus } from '../Locations/utils/LocationsReducer';
import { Loader } from '../uiComponents/CommonComponents/Loader';
import { ConfirmActionDialog } from '../uiComponents/ConfirmActionDialog';
import { InfoAlert } from '../uiComponents/infoAlert';
import { LEGACY_ACTIVITY_PERIOD_TIME_DEFAULTS } from './activityPeriodTimeSettingLegacy';
import {
  ACTIVITY_PERIOD_TIME_INCOMPLETE,
  findInvalidActivityQuestions,
} from './activityPeriodTimeValidation';
import { buildQuestionsOnMeterChange } from './assetsUtils/buildQuestionsOnMeterChange';
import { deleteAssociatedMetadata } from './assetsUtils/deleteAssociatedMetadata';
import {
  addAssociatedMetadataForConfig,
  addEmptyMeterConfiguration,
  addMeterConfigurationQuestion,
  cleanAssociatedMetadata,
  clearAllMeterConfigurations,
  deleteMeterConfiguration,
  deleteMeterConfigurationQuestion,
  normalizeMeterConfigurations,
  updateMeterConfigurationMeterId,
  updateMeterConfigurationQuestion,
} from './assetsUtils/meterConfigurationsUtils';
import { takesNoOffset, takesTwoOffsets } from './dateValidation/comparisons';
import { getOffsetBounds } from './dateValidation/constants';
import {
  DateQuestionField,
  DateQuestionModeType,
  DateQuestionReferenceField,
  RangeConditionType,
} from './dateValidation/types';
import { shouldKeepDateValidationFields } from './dateValidation/utils';
import { FormBuilderContext } from './FormBuilderProvider';
import { Header } from './header';
import {
  applyNumberValidationChangeInTable,
  applyNumberValidationChangeToQuestions,
} from './numberValidationCleanup';
import { Onboarding } from './onboarding';
import { QuestionList } from './questionList';
import { QuestionNavigator } from './questionNavigator';
import { questionTypes } from './questionTypes';
import { reBuildQuestion } from './reBuildQuestion';
import { RepeatedSectionsAlert } from './repeatedSectionsAlert';
import { SearchDataListModal } from './searchDataListModal';
import { ShareModal } from './shareModal';
import { disableOption } from './skipLogicComponent';

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

const customStyles: Record<string, SxProps<Theme>> = {
  infoAlertContainer: { mb: 2 },
};

const sourceUsesNestableLocations = (nestableLocations?: NestableLocations) =>
  !!nestableLocations &&
  (nestableLocations.all_locations_selected === true ||
    Object.keys(nestableLocations.selectedLocations ?? {}).length > 0);

const isEqualGroup = (type?: string) => ['equal', 'dif'].includes(type ?? '');

/**
 * Removes only the offset-related fields (offsetHours, offsetHoursToLeft, offsetHoursToRight)
 * while keeping dateValidationType and rangeCondition intact.
 */
export const removeDateQuestionValidationOffsetFields = (question: any) => {
  delete question.offsetHours;
  delete question.offsetHoursToLeft;
  delete question.offsetHoursToRight;
};

/**
 * Removes all date validation fields from a question object.
 * This helper function centralizes the cleanup logic used across multiple handlers.
 * It calls removeDateQuestionValidationOffsetFields to avoid code duplication.
 */
export const removeDateQuestionValidationFields = (question: any) => {
  removeDateQuestionValidationOffsetFields(question);
  delete question.referenceQuestionId;
  delete question.referenceQuestionIdToLeft;
  delete question.referenceQuestionIdToRight;
  delete question.dateValidationType;
  delete question.rangeCondition;
};

/**
 * Clears exactly the relative-range reference field(s) on `question` that point at an id in
 * `deletedIds`, leaving `dateValidationType`/`rangeCondition`/offsets untouched. Mirrors the
 * live per-panel guard in dateValidation.tsx, which clears a stale reference the same
 * field-at-a-time way once it drops out of the candidate list -- but that guard only runs while
 * the referencing question's own panel happens to be open. This is the delete-time counterpart,
 * covering every other question whose panel is not open at the moment its reference is deleted
 * (Bug Constructor: a deleted question left other questions' relative-range rules pointing at
 * nothing).
 */
const clearDanglingDateQuestionReferences = (question: any, deletedIds: Set<string>): any => {
  if (question.type !== 'date') return question;

  const referenceFields: DateQuestionReferenceField[] = [
    'referenceQuestionId',
    'referenceQuestionIdToLeft',
    'referenceQuestionIdToRight',
  ];
  const hasDanglingReference = referenceFields.some(
    (field) => question[field] && deletedIds.has(question[field]),
  );
  if (!hasDanglingReference) return question;

  const cleanedQuestion = { ...question };
  referenceFields.forEach((field) => {
    if (cleanedQuestion[field] && deletedIds.has(cleanedQuestion[field])) {
      delete cleanedQuestion[field];
    }
  });
  return cleanedQuestion;
};

/**
 * Sweeps a flat list of questions -- the top-level list, or one table's sub-question list --
 * for a relative-range reference into any id in `deletedIds`. Never called across scopes: a
 * top-level deletion sweeps the top-level list, a table sub-question deletion sweeps only that
 * table's own sub-questions, since a reference can never cross a table boundary (see
 * `logicQuestions` in dateValidation.tsx).
 */
export const sweepDanglingDateQuestionReferences = (
  questionsInScope: any[],
  deletedIds: Set<string>,
): any[] =>
  questionsInScope.map((question) => clearDanglingDateQuestionReferences(question, deletedIds));

export const cleanIncompleteDateValidationFields = (
  question: any,
  validReferenceIds?: Set<string>,
) => {
  if (question.type === 'date') {
    const isValidValidationType =
      question.dateValidationType === 'fixedRange' ||
      question.dateValidationType === 'relativeRange';

    if (!isValidValidationType) {
      removeDateQuestionValidationFields(question);
      return;
    }

    const hasRangeCondition = !!question.rangeCondition;

    if (isValidValidationType && !hasRangeCondition) {
      removeDateQuestionValidationFields(question);
      return;
    }

    const dateMode: 'datetime' | 'date' | 'time' = question.dateMode || 'datetime';

    // Belt-and-suspenders for the delete-time sweep above: if a dangling reference somehow
    // reaches save anyway, treat it as if it had never been picked, so the existing "relative
    // mode requires its reference(s)" rule below wipes the whole config the same way it already
    // does for a reference that was simply never set. `validReferenceIds` is the caller's live
    // id scope (top-level ids for a top-level question, one table's sub-question ids for a
    // table question) -- optional so this stays a no-op wherever a caller has no scope to check
    // against.
    const isDangling = (referenceId?: string) =>
      !!referenceId && !!validReferenceIds && !validReferenceIds.has(referenceId);

    // The single decision point for whether this configuration is complete enough to persist:
    // routes through the same predicate the panel's inline field error and hint banner already
    // show (hasDateValidationErrors, via shouldKeepDateValidationFields), so the save path can
    // no longer diverge from what the panel flags -- including the relative mode's reference
    // requirement and the unanswerable notBetween/time window, neither of which a narrower,
    // save-path-only reimplementation of the offset bounds check would catch (DS-4175 review,
    // DS-4176 review).
    if (
      !shouldKeepDateValidationFields({
        selectedRangeCondition: question.rangeCondition,
        dateMode,
        dateValidationType: question.dateValidationType,
        offsetHours: question.offsetHours,
        offsetHoursToLeft: question.offsetHoursToLeft,
        offsetHoursToRight: question.offsetHoursToRight,
        referenceQuestionId: isDangling(question.referenceQuestionId)
          ? ''
          : question.referenceQuestionId,
        referenceQuestionIdToLeft: isDangling(question.referenceQuestionIdToLeft)
          ? ''
          : question.referenceQuestionIdToLeft,
        referenceQuestionIdToRight: isDangling(question.referenceQuestionIdToRight)
          ? ''
          : question.referenceQuestionIdToRight,
      })
    ) {
      removeDateQuestionValidationFields(question);
    }
  }
};

export const FormBuilder = () => {
  const mediaMatch = window.matchMedia('(min-width: 768px)');
  const [matches, setMatches] = useState(mediaMatch.matches);
  const { formId: urlFormId } = useParams();
  const styles: StyleSheet = {
    mainContainer: {
      height: '100%',
      width: '100%',
    },
    headerContainer: { marginTop: 10 },
    bodyContainer: {
      display: 'flex',
      gap: '10px',
      padding: '0px 20px',
      boxSizing: 'border-box',
    },
    leftContainer: {
      flex: 1,
    },
    middleContainer: {
      flex: 2,
      marginTop: '5px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    rightContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
    },
    dialogTitle: {
      fontSize: theme.typography.h2.fontSize,
      textAlign: 'justify',
      fontWeight: 'bold',
      marginTop: 10,
      color: 'black',
    },
    dialogInstruction: {
      fontSize: theme.typography.h4.fontSize,
      textAlign: 'center',
    },
  };
  const [formName, setFormName] = useState('');
  const [originalFormName, setOriginalFormName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [logicQuestions, setLogicQuestions] = useState([]);
  const [dataSources, setDataSources] = useState([{ tag: 'Nueva Lista de datos', value: 'none' }]);
  const [zoomedId, setZoomedId] = useState('1');
  const [zoomedIdInTable, setZoomedIdInTable] = useState(undefined);
  const [hoveredId, setHoveredId] = useState(undefined);
  const [scrolledIdx, setScrolledIdx] = useState(undefined);
  const [expanded, setExpanded] = useState(false);
  const [expandedInTable, setExpandedInTable] = useState(false);
  const lang = useLanguage() as any;
  const { enqueueSnackbar } = useSnackbar();
  const [disableDelete, setDisableDelete] = useState(false);
  const [windowHeight, setWindowHeight] = useState(0);
  const [disableButton, setDisableButton] = useState(false);
  const [paying, setPaying] = useState(false);
  const [openedAlert, setOpenedAlert] = useState(false);
  // Ids of the activity questions a save attempt found incomplete. Errors only
  // appear after a failed save: turning them on earlier would dirty the initial
  // state of a new question, which starts deliberately unconfigured. The set is
  // keyed by id rather than a single boolean so that marking one question does
  // not mark the ones added afterwards.
  const [invalidActivityQuestionIds, setInvalidActivityQuestionIds] = useState<Set<string>>(
    new Set(),
  );
  const [helper, setHelper] = useState(undefined);
  const [availableFileUpload, setAvailableFileUpload] = useState(false);
  const [noFormUser, setNoFormUser] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [linkToken, setLinkToken] = useState(undefined);
  const [formId, setFormId] = useState(urlFormId);
  const [formUpdatesLockedAt, setFormUpdatesLockedAt] = useState(null);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [showDynamicSignatureModifyingAlert, setShowDynamicSignatureModifyingAlert] =
    useState(false);
  const [listManagerFeature, setListManagerFeature] = useState(false);
  const [sectionStates, setSectionStates] = useState({});
  const [combineAlert, setCombineAlert] = useState(false);
  const [emptySubformAlert, setEmptySubformAlert] = useState(false);
  const [combineAlertText, setCombineAlertText] = useState('');
  const [subformStates, setSubformStates] = useState({});
  const [zoomAfterAction, setZoomAfterAction] = useState(undefined);
  const [scrollAfterAction, setScrollAfterAction] = useState(undefined);
  const [zoomInTableAfterAction, setZoomInTableAfterAction] = useState(undefined);
  const [tableLogicQuestions, setTableLogicQuestions] = useState<{
    [key: string]: SkipLogicQuestion[];
  }>({});
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const sectionCount = useRef(0);
  const latestSectionId = useRef(0);
  const newId = useRef(1);
  const { searchDataListModalOpen, setQuestions: setContextQuestions } =
    useContext(FormBuilderContext);

  // Feature Flags
  const { checkFlag } = useFeatureFlags();
  const { blockFormBuilder } = useProjectConfig();
  const navigate = useNavigate();
  const searchDataListModalFeature = checkFlag('SearchDataListModal');
  const QRAllowed = checkFlag('OnboardingQR');
  const formBuilderLogs = checkFlag('FormBuilderLogs');
  const ChecklistConfig = checkFlag('ChecklistConfig');
  const dynamicSignatureFeatureFlag = checkFlag('DynamicSignature');
  const customWorkdayDurationFlag = checkFlag('CustomWorkdayDuration');

  // Tracker:
  useEffect(() => {
    datascopeTracker('Open Form Builder', { source: 'FORM_BUILDER' }, 'AMP');
  }, []);

  // Sync questions with context
  useEffect(() => {
    setContextQuestions(questions);
  }, [questions, setContextQuestions]);

  const { setInitialDataToTrack, handleCompareAndLogData } = useChangeLogger();

  const getQuestionSection = useCallback((index: number, questions: QuestionInterface[]) => {
    for (let i = index - 1; i >= 0; i--) {
      if (questions[i].type === 'section') return questions[i].id;
    }
    return undefined;
  }, []);
  // draggableId = questionId of recipient ; sourceId = questionId of dragged Q ; sourceIndex = position in list of dragged Q
  // errorCode = 0 -> correct combination
  // errorCode = 1 -> attempted combine into a non-subform question
  // errorCode = 2 -> attempted subform into subform combine
  const handleCombine = useCallback(
    (draggableId: number, sourceId: number, sourceIndex: number) => {
      let errorCode = -1;
      let subformIndex = -1;
      setQuestions((prevQuestions) => {
        const newQuestions = [...prevQuestions];
        const destination = draggableId;
        subformIndex = newQuestions.findIndex((q) => q.id === destination);
        const subform = newQuestions[subformIndex];
        if (!(subform && subformIndex >= 0 && subform.type === 'table')) {
          errorCode = 1;
          return newQuestions;
        }
        const sourceQuestion = newQuestions.find((question) => question.id === sourceId);
        if (sourceQuestion.type !== 'table') {
          if (sourceIndex < subformIndex) subformIndex--;
          const newTableQuestions = [...subform.questions];
          newQuestions.splice(sourceIndex, 1);
          newTableQuestions.push(sourceQuestion);
          newQuestions[subformIndex].questions = newTableQuestions;
          errorCode = 0;
        } else errorCode = 2;
        return newQuestions;
      });
      setZoomedIdInTable(String(sourceId));
      setZoomAfterAction(`${draggableId}`);
      setScrollAfterAction(subformIndex !== -1 ? subformIndex : 0);
      return errorCode;
    },
    [setZoomedIdInTable, setZoomAfterAction, setScrollAfterAction],
  );

  // Replaces the question with the given id with the given question, updated by the given function
  // if a tableId is given, then it replaces the question in the table question list
  // the update function should return the same type as the one given
  function updateQuestion<QuestionType>(
    update: (question: QuestionType) => QuestionType,
    questionId: string,
    tableId?: string,
  ) {
    setQuestions(reBuildQuestion(update, questionId, tableId));
  }

  // Remove ONE question (without deleting) from subform and return it to form
  const subQuestionRemove = useCallback(
    (subformId: string, deletedQuestionIndex: number, deletedQuestionId: string) => {
      let subformEmptied = false;
      const subformIndex = questions.findIndex((q) => q.id === subformId);
      let zoomInTable;
      setQuestions((prevQuestions) => {
        const newQuestions = [...prevQuestions];
        const subform = newQuestions[subformIndex];
        const newTableQuestions = [...subform.questions];
        if (sectionCount.current === 0) {
          const deletedQuestion = newTableQuestions[deletedQuestionIndex];
          newTableQuestions.splice(deletedQuestionIndex, 1);
          newQuestions.push(deletedQuestion);
        } else {
          const deletedQuestion = newTableQuestions.splice(deletedQuestionIndex, 1)[0];
          newQuestions.splice(subformIndex + 1, 0, deletedQuestion);
        }
        zoomInTable = newTableQuestions[0] ? newTableQuestions[0].id : null;
        if (newTableQuestions.length === 0) {
          subformEmptied = true;
          newId.current += 1;
          const subformSection = getQuestionSection(subformIndex, newQuestions);
          newTableQuestions.push({
            id: `${newId.current}`,
            name: `${lang.types.text} ${newId.current}`,
            type: 'text',
            sectionId: subformSection,
          });
        }
        newQuestions[subformIndex].questions = newTableQuestions;
        return newQuestions;
      });
      if (zoomInTable) setZoomInTableAfterAction(zoomInTable);
      setZoomAfterAction(deletedQuestionId);
      setScrollAfterAction(subformIndex + 1);
      if (subformEmptied) {
        setCombineAlert(false);
        setEmptySubformAlert(true);
      }
    },
    [
      questions,
      setQuestions,
      setZoomInTableAfterAction,
      setZoomAfterAction,
      setScrollAfterAction,
      getQuestionSection,
      newId,
      sectionCount,
      lang.types.text,
    ],
  );

  const combineAlertHandler = (message: string) => {
    setCombineAlertText(message);
    setCombineAlert(true);
  };

  useEffect(() => {
    if (zoomAfterAction !== undefined) {
      setZoomedId(zoomAfterAction);
    }
  }, [zoomAfterAction]);

  useEffect(() => {
    if (scrollAfterAction !== undefined) {
      setScrolledIdx(scrollAfterAction);
    }
  }, [scrollAfterAction]);

  useEffect(() => {
    if (zoomInTableAfterAction !== undefined) {
      setZoomedIdInTable(zoomInTableAfterAction);
    }
  }, [zoomInTableAfterAction]);

  // Remove ALL questions (without deleting) and return them to the form
  const subformEmpty = (subformId: string, firstSubquestionId: string) => {
    const subformIndex = questions.findIndex((q) => q.id === subformId);
    let subformEmptied = false;
    setQuestions((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      const subform = newQuestions[subformIndex];
      if (sectionCount.current === 0) {
        subform.questions.forEach((deletedQuestion: any) => {
          newQuestions.push(deletedQuestion);
        });
      } else {
        let insertIndex = subformIndex + 1;
        subform.questions.forEach((deletedQuestion: any) => {
          newQuestions.splice(insertIndex, 0, deletedQuestion);
          insertIndex++;
        });
      }
      subformEmptied = true;
      subform.questions.splice(0, subform.questions.length);
      setCombineAlert(false);
      setEmptySubformAlert(true);
      newId.current += 1;
      const subformSection: any = getQuestionSection(subformIndex, newQuestions);
      subform.questions.push({
        id: `${newId.current}`,
        name: `${lang.types.text} ${newId.current}`,
        type: 'text',
        sectionId: subformSection,
      });
      return newQuestions;
    });
    if (subformEmptied) {
      setZoomInTableAfterAction(`${newId.current}`);
      setZoomAfterAction(firstSubquestionId);
      setScrollAfterAction(subformIndex + 1);
    }
  };

  // Controls everything that happens when a drag & drop occurs
  const onDragEndHandler = useCallback((event: any, dndSource: string) => {
    const { destination, source, draggableId, combine } = event;
    if (combine) {
      const combineError = handleCombine(combine.draggableId, draggableId, source.index);
      if (combineError) {
        setEmptySubformAlert(false);
        if (combineError === 1) combineAlertHandler(lang.form_builder.combineAlert1);
        else if (combineError === 2) combineAlertHandler(lang.form_builder.combineAlert2);
      }
    }

    if (!destination) return;

    if (destination.index === source.index) {
      return;
    }

    const subformSwap =
      destination.droppableId.startsWith('subform') && source.droppableId.startsWith('subform');

    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      let totalQuestions: any[] = [];
      newQuestions.forEach((q: any) => {
        totalQuestions.push(q);
        if (q.type === 'table') {
          totalQuestions = totalQuestions.concat(q.questions);
        }
      });

      const draggedQuestionIndex = subformSwap
        ? totalQuestions.findIndex((question) => question.id === draggableId)
        : newQuestions.findIndex((question) => question.id === draggableId);
      const draggedQuestion = subformSwap
        ? totalQuestions[draggedQuestionIndex]
        : newQuestions[draggedQuestionIndex];

      if (destination.index === 0 && newQuestions[0].type === 'section' && !subformSwap) {
        return newQuestions;
      }

      if (subformSwap) {
        const subformId = destination.droppableId.split('-')[1];
        newQuestions = newQuestions.map((question) => {
          if (question.id === subformId) {
            const newQuestion = { ...question };
            newQuestion.questions.splice(source.index, 1);
            newQuestion.questions.splice(destination.index, 0, draggedQuestion);
            return newQuestion;
          }
          return question;
        });
      } else {
        newQuestions.splice(draggedQuestionIndex, 1);
        newQuestions.splice(destination.index, 0, draggedQuestion);
        draggedQuestion.sectionId = getQuestionSection(destination.index, newQuestions);
      }
      if (draggedQuestion.type === 'table') {
        draggedQuestion.questions.forEach((question: any) => {
          question.sectionId = draggedQuestion.sectionId;
        });
      }
      return newQuestions;
    });

    if (dndSource === 'navigator') {
      setScrolledIdx(destination.index);
    }
  }, []);

  // Creates an objects with sections as keys and an array with all the
  // subforms they contain as values.
  const sectionSubforms = () => {
    const sectionSubs: any = {};
    let currentSection: string;
    questions.forEach((q: any, i: number) => {
      if (q.type === 'section') {
        currentSection = q.id;
        sectionSubs[currentSection] = [];
      } else if (q.type === 'table' && Object.keys(subformStates).length !== 0)
        sectionSubs[currentSection].push(i + q.questions.length);
    });
    return sectionSubs;
  };

  // Defines initial states for sections and subforms
  useEffect(() => {
    if (Object.keys(sectionStates).length === 0) {
      const initialSectionStates: any = {};
      // Sections are open (true) by default
      questions.forEach((q: any) => {
        if (q.type === 'section') initialSectionStates[q.id] = true;
      });
      setSectionStates(initialSectionStates);
    }
    if (Object.keys(subformStates).length === 0) {
      setSubformStates(sectionSubforms());
    }
  }, [questions]);

  const onZoomedHandler = useCallback((id: string) => {
    setZoomedId(() => id);
  }, []);

  const onHoveredHandler = useCallback((id: string) => {
    setHoveredId(() => id);
  }, []);

  const onScrolledIdxHandler = useCallback((index: number) => {
    setScrolledIdx(() => index);
  }, []);

  const onExpandedHandler = useCallback((table: boolean) => {
    if (table) return setExpandedInTable((prev) => !prev);
    setExpanded((prevData) => !prevData);
  }, []);

  const onAddHandler = useCallback(
    (index: number) => {
      newId.current += 1;
      const newQuestion: QuestionInterface = {
        id: `${newId.current}`,
        name: `${lang.types.text} ${newId.current}`,
        type: 'text',
      };
      setQuestions((prevQuestions) => {
        const newQuestions = [...prevQuestions];
        newQuestion['sectionId'] =
          sectionCount.current > 0 ? getQuestionSection(index, newQuestions) : null;
        newQuestions.splice(index + 1, 0, newQuestion);
        return newQuestions;
      });
      setZoomedId(newId.current.toString());
      setScrolledIdx(index + 1);
    },
    [lang.types.text],
  );

  const onDeleteHandler = useCallback((id: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.filter((question) => question.id !== id);
      // A surviving top-level question's relative-range validation may point at the one just
      // deleted; sweep the top-level list so it doesn't keep an unresolvable reference.
      return sweepDanglingDateQuestionReferences(newQuestions, new Set([id]));
    });
  }, []);

  const duplicateSectionHandler = useCallback(
    (duplicatedSectionId: string) => {
      latestSectionId.current += 1;
      sectionCount.current += 1;
      setQuestions((prevQuestions) => {
        const newQuestions = [...prevQuestions];
        const questionsToCopy = [];
        let auxIndex = 0;
        let copiedSectionIndex = prevQuestions.length;
        for (let idx = 0; idx <= newQuestions.length; idx++) {
          if (idx > copiedSectionIndex) {
            auxIndex += 1;
            if (idx < newQuestions.length && newQuestions[idx].type !== 'section') {
              newId.current += 1;
              const copiedQuestion = { ...newQuestions[idx] };
              copiedQuestion.id = `${newId.current}`;
              copiedQuestion.name = `${lang.form_builder.copyName} ` + copiedQuestion.name;
              copiedQuestion.sectionId = `s${latestSectionId.current}`;
              if (copiedQuestion.type === 'table') {
                copiedQuestion.questions = copiedQuestion.questions.map((q: any) => {
                  newId.current += 1;
                  const newQ = { ...q };
                  newQ.id = `${newId.current}`;
                  newQ.name = `${lang.form_builder.copyName} ` + newQ.name;
                  newQ.sectionId = `s${latestSectionId.current}`;
                  return newQ;
                });
              }
              questionsToCopy.push(copiedQuestion);
            }
          }
          if (
            idx === newQuestions.length ||
            (newQuestions[idx].type === 'section' && idx > copiedSectionIndex)
          ) {
            break;
          }
          if (newQuestions[idx].id === duplicatedSectionId) {
            copiedSectionIndex = idx;
            auxIndex = idx;
          }
        }
        newQuestions.splice(auxIndex, 0, {
          id: `s${latestSectionId.current}`,
          type: 'section',
          name: `${lang.form_builder.copyName} ${prevQuestions[copiedSectionIndex].name}`,
          order: latestSectionId.current,
        });
        newQuestions.splice(auxIndex + 1, 0, ...questionsToCopy);
        addSectionState(`s${latestSectionId.current}`);
        return newQuestions;
      });
      const current = `${newId.current}`;
      setZoomedId(current);
    },
    [lang.form_builder.sectionName],
  );

  const onDuplicateHandler = useCallback(
    (id: string, index: number) => {
      newId.current += 1;
      const current = `${newId.current}`;
      setQuestions((prevQuestions) => {
        const newQuestions = [...prevQuestions];
        const copiedQuestion = {
          ...newQuestions.find((question) => question.id === id),
        };
        copiedQuestion.id = `${newId.current}`;
        copiedQuestion.name = `${lang.form_builder.copyName} ` + copiedQuestion.name;
        if (copiedQuestion.type === 'table') {
          copiedQuestion.questions = copiedQuestion.questions.map((q: any) => {
            newId.current += 1;
            const newQ = { ...q };
            newQ.id = `${newId.current}`;
            newQ.name = `${lang.form_builder.copyName} ` + newQ.name;
            return newQ;
          });
        }
        newQuestions.splice(index + 1, 0, copiedQuestion);

        return newQuestions;
      });
      setZoomedId(current);
    },
    [lang.form_builder.copyName],
  );

  const addSubformState = (sectionId: string, subformIndex: number) => {
    setSubformStates((prevState) => {
      const newState: any = { ...prevState };
      if (newState[sectionId]) newState[sectionId].push(subformIndex);
      return newState;
    });
  };

  const deleteSubformState = (sectionId: string, subformIndex: number) => {
    setSubformStates((prevState) => {
      const newState: any = { ...prevState };
      newState[sectionId] = newState[sectionId].filter((subform: any) => subform !== subformIndex);
      return newState;
    });
  };

  const onTypeChange = useCallback(
    (id: string, type: string) => {
      const { good, regular, bad } = lang.form_builder;
      newId.current += 1;
      setZoomedId(`${newId.current}`);
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question, i) => {
          if (question.id === id) {
            const prevType = question.type;
            if (prevType === 'table' && question.sectionId)
              deleteSubformState(question.sectionId, i);
            const newQuestion = { ...questionTypes[type] };
            if (!customWorkdayDurationFlag && type === 'select_activity_period_time') {
              Object.assign(newQuestion, LEGACY_ACTIVITY_PERIOD_TIME_DEFAULTS);
            }
            newQuestion.id = `${newId.current}`;
            if (question.name === `${lang.types[question.type]} ${question.id}`) {
              newQuestion.name = `${lang.types[type]} ${newId.current}`;
            } else {
              newQuestion.name = question.name;
            }
            if (question.description) newQuestion.description = question.description;

            if (type === 'table') {
              newId.current += 1;
              newQuestion.questions = [];
              newQuestion.questions.push({
                id: 0,
                name: `${lang.types.text}`,
                type: 'text',
                sectionId: question.sectionId,
              });
              newQuestion.questions[0].name += ` ${newId.current}`;
              newQuestion.questions[0].id = `${newId.current}`;
              addSubformState(question.sectionId, i);
              if (!paying) newQuestion.max_subforms = '5';
            }
            if (type === 'multi_option') {
              newQuestion.options = [good, regular, bad];
            }
            if (type === 'select_option_metadata') {
              // We will change it to select_option_metadata_comments, since it's the new default:
              newQuestion.type = 'select_option_metadata_comments';
              newQuestion.maxImages = ChecklistConfig ? 1 : 10;
              newQuestion.options = [good, regular, bad].map((option: string) => {
                return { option, req_text: false, req_image: false, req_finding: false };
              });
            }
            if (type === 'photo') {
              newQuestion.cameraQuality = '0.6';
              newQuestion.maxHeight = '600';
              newQuestion.maxWidth = '600';
            }
            newQuestion.sectionId = question.sectionId;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
      if (type === 'table') setZoomedIdInTable(`${newId.current + 1}`);
    },
    [lang.form_builder, lang.types, paying, ChecklistConfig, customWorkdayDurationFlag],
  );

  const onNameChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.name = value;
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDescriptionChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.description = value;
          if (value === '') delete changedQuestion.description;
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onStaticTextChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.static_text = value;
          if (value === '') delete changedQuestion.static_text;
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDateModeChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.dateMode = value;
          if (value === 'date') changedQuestion.dateFormat = 'DD-MM-YYYY';
          if (value === 'datetime') changedQuestion.dateFormat = 'DD-MM-YYYY HH:mm';
          if (value === 'time') changedQuestion.dateFormat = 'HH:mm';
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onRequiredChange = useCallback((id: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.req = changedQuestion.req ? undefined : '1';
          if (changedQuestion.req === undefined) {
            delete changedQuestion.req;
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onTextValidationChange = (
    questionId: string,
    validation: 'email' | 'rut-chile' | 'container' | null,
    tableId?: string,
  ) => {
    updateQuestion(
      (question: TextQuestion) => {
        delete question.mail;
        question.containerValidation = false;
        question.rutValidation = false;
        switch (true) {
          case validation === 'email':
            question.mail = '1';
            break;
          case validation === 'rut-chile':
            question.rutValidation = true;
            break;
          case validation === 'container':
            question.containerValidation = true;
            break;
          case validation === null:
            break;
        }
        return question;
      },
      questionId,
      tableId,
    );
  };

  const onNumberPercentageChange = (
    questionId: string,
    show: 'percent' | 'sampling' | null,
    tableId?: string,
  ) => {
    updateQuestion(
      (question: MetadataNumberQuestion) => {
        switch (true) {
          case show === 'percent':
            question.showPercent = '1';
            delete question.showSampling;
            break;
          case show === 'sampling':
            question.showSampling = '1';
            delete question.showPercent;
            break;
          case show === null:
            delete question.showPercent;
            delete question.showSampling;
            break;
        }
        return question;
      },
      questionId,
      tableId,
    );
  };

  const onBarcodeTypeChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.barcodeType = value;
          if (value === '0') {
            delete changedQuestion.barcodeType;
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDisableGalleryChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.disableGallery = changedQuestion.disableGallery ? undefined : '1';
          if (changedQuestion.disableGallery === undefined) {
            delete changedQuestion.disableGallery;
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onMultiPhotoChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.type = changedQuestion.type === 'multi_photo' ? 'photo' : 'multi_photo';
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onChecklistSettingsChange = (
    questionId: string,
    disableGallery = false,
    maxImages = 1,
    tableId?: string,
  ) => {
    updateQuestion(
      (question: MetadataAlternativeCommentsQuestion) => {
        question.disableGallery = disableGallery;
        question.maxImages = maxImages;
        return question;
      },
      questionId,
      tableId,
    );
  };

  /** Clears all meter configs when value is '-1'; otherwise applies meter change and cleans up linked questions' associatedMetadata. */
  const onMeterChange = useCallback((questionId: string, value: string, tableId?: string) => {
    setQuestions((prevQuestions) => {
      if (value === '-1') {
        const { updatedQuestions, questionIdsToClean } = clearAllMeterConfigurations(
          questionId,
          tableId,
          prevQuestions,
        );
        return cleanAssociatedMetadata(questionIdsToClean, tableId, updatedQuestions);
      }

      const { updatedQuestions, questionIdsToDeleteAssociatedMetadata } =
        buildQuestionsOnMeterChange(questionId, tableId, prevQuestions, value);

      return deleteAssociatedMetadata(
        questionIdsToDeleteAssociatedMetadata,
        tableId,
        updatedQuestions,
      );
    });
  }, []);

  /** Adds a new empty meter configuration to the question's meterConfigurations. */
  const onAddMeterConfiguration = useCallback((questionId: string, tableId?: string) => {
    setQuestions((prevQuestions) => addEmptyMeterConfiguration(questionId, tableId, prevQuestions));
  }, []);

  /** Removes one meter configuration by index and cleans associatedMetadata on its linked questions. */
  const onDeleteMeterConfiguration = useCallback(
    (questionId: string, configIndex: number, tableId?: string) => {
      setQuestions((prevQuestions) => {
        const { updatedQuestions, questionIdsToClean } = deleteMeterConfiguration(
          questionId,
          configIndex,
          tableId,
          prevQuestions,
        );
        return cleanAssociatedMetadata(questionIdsToClean, tableId, updatedQuestions);
      });
    },
    [],
  );

  /** Updates the meterId of one meter configuration; clears its associated questions and cleans their associatedMetadata. */
  const onMeterConfigurationMeterChange = useCallback(
    (questionId: string, configIndex: number, newMeterId: string, tableId?: string) => {
      setQuestions((prevQuestions) => {
        const { updatedQuestions, questionIdsToClean } = updateMeterConfigurationMeterId(
          questionId,
          configIndex,
          newMeterId,
          tableId,
          prevQuestions,
        );
        return cleanAssociatedMetadata(questionIdsToClean, tableId, updatedQuestions);
      });
    },
    [],
  );

  /** Adds an empty slot to a meter configuration's associatedQuestions. */
  const onAddMeterConfigurationQuestion = useCallback(
    (questionId: string, configIndex: number, tableId?: string) => {
      setQuestions((prevQuestions) =>
        addMeterConfigurationQuestion(questionId, configIndex, tableId, prevQuestions),
      );
    },
    [],
  );

  /** Updates one associated-question slot (which number/location question updates this config); sets/cleans associatedMetadata on the linked questions. */
  const onUpdateMeterConfigurationQuestion = useCallback(
    (
      questionId: string,
      configIndex: number,
      questionSlotIndex: number,
      value: string,
      tableId?: string,
    ) => {
      setQuestions((prevQuestions) => {
        const { updatedQuestions, questionIdToClean, questionInfoToAssociate } =
          updateMeterConfigurationQuestion(
            questionId,
            configIndex,
            questionSlotIndex,
            value,
            tableId,
            prevQuestions,
          );
        let result = updatedQuestions;
        if (questionInfoToAssociate) {
          result = addAssociatedMetadataForConfig(
            questionInfoToAssociate,
            questionId,
            result,
            tableId,
          );
        }
        if (questionIdToClean) {
          result = cleanAssociatedMetadata([questionIdToClean], tableId, result);
        }
        return result;
      });
    },
    [],
  );

  /** Removes one associated-question slot from a meter configuration and cleans that question's associatedMetadata. */
  const onDeleteMeterConfigurationQuestion = useCallback(
    (questionId: string, configIndex: number, questionSlotIndex: number, tableId?: string) => {
      setQuestions((prevQuestions) => {
        const { updatedQuestions, questionIdToClean } = deleteMeterConfigurationQuestion(
          questionId,
          configIndex,
          questionSlotIndex,
          tableId,
          prevQuestions,
        );
        return cleanAssociatedMetadata(
          questionIdToClean ? [questionIdToClean] : [],
          tableId,
          updatedQuestions,
        );
      });
    },
    [],
  );

  const onCreateAssetQuestion = useCallback(
    (
      tableId: string | undefined,
      type: 'number' | 'select_metadata',
      associationContext?: { parentQuestionId: string; configIndex: number; slotIndex: number },
    ) => {
      newId.current += 1;
      const newQuestionId = `${newId.current}`;
      const newQuestion: QuestionInterface = {
        id: newQuestionId,
        name: `${lang.types[type]} ${newId.current}`,
        type,
      };
      let insertedIdxForScroll: number | null = null;
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        if (tableId) {
          newQuestions = newQuestions.map((question) => {
            if (question.id === tableId) {
              const newTableQuestions = [...question.questions];
              const parentQuestionId = associationContext?.parentQuestionId;
              const parentIdx = parentQuestionId
                ? newTableQuestions.findIndex((q) => q.id === parentQuestionId)
                : -1;

              newQuestion['sectionId'] = question.sectionId;
              if (parentIdx >= 0) {
                newTableQuestions.splice(parentIdx + 1, 0, newQuestion);
                insertedIdxForScroll = parentIdx + 1;
              } else {
                newTableQuestions.push(newQuestion);
                insertedIdxForScroll = newTableQuestions.length - 1;
              }
              return { ...question, questions: newTableQuestions };
            }
            return question;
          });
        } else {
          const parentQuestionId = associationContext?.parentQuestionId;
          const parentIdx = parentQuestionId
            ? newQuestions.findIndex((q) => q.id === parentQuestionId)
            : -1;

          newQuestion['sectionId'] =
            sectionCount.current > 0
              ? parentIdx >= 0
                ? getQuestionSection(parentIdx, newQuestions)
                : getQuestionSection(newQuestions.length, newQuestions)
              : null;

          if (parentIdx >= 0) {
            newQuestions.splice(parentIdx + 1, 0, newQuestion);
            insertedIdxForScroll = parentIdx + 1;
          } else {
            newQuestions.push(newQuestion);
            insertedIdxForScroll = newQuestions.length - 1;
          }
        }
        // Link the new question to the meter configuration
        if (associationContext) {
          const { parentQuestionId, configIndex, slotIndex } = associationContext;
          const { updatedQuestions, questionIdToClean, questionInfoToAssociate } =
            updateMeterConfigurationQuestion(
              parentQuestionId,
              configIndex,
              slotIndex,
              newQuestionId,
              tableId,
              newQuestions,
            );
          newQuestions = updatedQuestions;
          if (questionIdToClean) {
            newQuestions = cleanAssociatedMetadata([questionIdToClean], tableId, newQuestions);
          }
          if (questionInfoToAssociate) {
            newQuestions = addAssociatedMetadataForConfig(
              questionInfoToAssociate,
              parentQuestionId,
              newQuestions,
              tableId,
            );
          }
        }
        return newQuestions;
      });
      if (tableId) {
        setZoomedIdInTable(newQuestionId);
      } else {
        setZoomedId(newQuestionId);
        setScrolledIdx(insertedIdxForScroll ?? 0);
      }
    },
    [],
  );

  const onOptionChange = useCallback((id: string, index: number, action: string, value: string) => {
    setQuestions((prevQuestion) => {
      let newQuestions = [...prevQuestion];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = {
            ...question,
            options: [...(question.options ? question.options : [])],
          };
          if (
            changedQuestion.type === 'select_option_metadata_comments' &&
            question.options.some((option: unknown) => typeof option === 'string')
          ) {
            changedQuestion.options = question.options.map((option: unknown) => {
              return { option, req_text: false, req_image: false, req_finding: false };
            });
          }
          switch (action) {
            case 'create':
              if (changedQuestion.type === 'select_option_metadata_comments') {
                changedQuestion.options.push({
                  option: value,
                  req_text: false,
                  req_image: false,
                  req_finding: false,
                });
              } else changedQuestion.options.push(value);
              break;
            case 'update':
              if (changedQuestion.type === 'select_option_metadata_comments') {
                changedQuestion.options[index].option = value;
              } else changedQuestion.options[index] = value;
              break;
            case 'delete':
              changedQuestion.options.splice(index, 1);
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onMetadataCodeChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      const questionIdsToDeleteAssociatedMetadata: string[] = [];
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          delete changedQuestion.nestableLocations;
          delete changedQuestion.selectedAssets;
          if (value === 'none') {
            delete changedQuestion.metadata;
            changedQuestion.listChoices = [];
          } else {
            if (changedQuestion.metadata === 'assets') {
              const configs =
                (changedQuestion as MetadataOptionsQuestion).meterConfigurations ?? [];
              configs.forEach((cfg: MeterConfiguration) =>
                questionIdsToDeleteAssociatedMetadata.push(...(cfg.associatedQuestions ?? [])),
              );
              delete changedQuestion.meterConfigurations;
              delete changedQuestion.meterId;
              delete changedQuestion.updateData;
              delete changedQuestion.associatedQuestions;
            }
            changedQuestion.metadata = value;
            delete changedQuestion.listChoices;
          }
          return changedQuestion;
        }
        return question;
      });
      if (questionIdsToDeleteAssociatedMetadata.length > 0) {
        newQuestions = newQuestions.map((question) => {
          if (!questionIdsToDeleteAssociatedMetadata.includes(question.id)) return question;
          const updatedQuestion = { ...question };
          delete updatedQuestion.associatedMetadata;
          return updatedQuestion;
        });
      }
      return newQuestions;
    });
  }, []);

  const onListChoicesChange = useCallback(
    (id: string, index: number, action: string, value: string) => {
      setQuestions((prevQuestion) => {
        let newQuestions = [...prevQuestion];
        newQuestions = newQuestions.map((question) => {
          if (question.id === id) {
            const changedQuestion = {
              ...question,
              listChoices: [...(question.listChoices ? question.listChoices : [])],
            };
            if (action === 'create') changedQuestion.listChoices.push(value);
            if (action === 'update') changedQuestion.listChoices[index] = value;
            if (action === 'delete') changedQuestion.listChoices.splice(index, 1);
            return changedQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onAddInTableHandler = useCallback(
    (index: number, tableId: string) => {
      newId.current += 1;
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        const tableSection = newQuestions.find((q) => q.id === tableId).sectionId;
        const newQuestionInTable = {
          id: `${newId.current}`,
          name: `${lang.types.text} ${newId.current}`,
          type: 'text',
          sectionId: tableSection,
        };
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            const newTableQuestions = [...newQuestion.questions];
            newTableQuestions.splice(index + 1, 0, newQuestionInTable);
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
      setZoomedIdInTable(newId.current.toString());
    },
    [lang.types.text],
  );

  const onDeleteInTableHandler = useCallback((id: string, tableId: string | number) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          if (newTableQuestions.length !== 1) {
            newTableQuestions = newTableQuestions.filter((q) => q.id !== id);
            // Scoped to this table's own sub-questions: a relative-range reference never
            // crosses a table boundary, so only siblings within the same table can be dangling.
            newTableQuestions = sweepDanglingDateQuestionReferences(
              newTableQuestions,
              new Set([id]),
            );
          }
          if (newTableQuestions.some((q) => q.req === '1')) {
            newQuestion.req = '1';
          } else {
            delete newQuestion.req;
          }
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDuplicateInTableHandler = useCallback(
    (id: string, index: number, tableId: string) => {
      newId.current += 1;
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            const newTableQuestions = [...newQuestion.questions];

            const copiedQuestion = {
              ...newTableQuestions.find((question) => question.id === id),
            };
            copiedQuestion.id = `${newId.current}`;
            copiedQuestion.name = `${lang.form_builder.copyName} ` + copiedQuestion.name;
            newTableQuestions.splice(index + 1, 0, copiedQuestion);

            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
      setZoomedIdInTable(newId.current.toString());
    },
    [lang.form_builder.copyName],
  );

  const onTypeInTableChange = useCallback(
    (id: string, type: string, tableId: string) => {
      const { good, regular, bad } = lang.form_builder;
      newId.current += 1;
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = { ...questionTypes[type] };
                if (!customWorkdayDurationFlag && type === 'select_activity_period_time') {
                  Object.assign(changedQuestion, LEGACY_ACTIVITY_PERIOD_TIME_DEFAULTS);
                }
                changedQuestion.id = `${newId.current}`;
                if (q.name === `${lang.types[q.type]} ${q.id}`) {
                  changedQuestion.name = `${lang.types[type]} ${newId.current}`;
                } else {
                  changedQuestion.name = q.name;
                }
                if (q.description) changedQuestion.description = q.description;
                if (type === 'multi_option') {
                  changedQuestion.options = [good, regular, bad];
                }
                if (type === 'select_option_metadata') {
                  // We will change it to select_option_metadata_comments, since it's the new default:
                  changedQuestion.type = 'select_option_metadata_comments';
                  changedQuestion.maxImages = ChecklistConfig ? 1 : 10;
                  changedQuestion.options = [good, regular, bad].map((option: string) => {
                    return { option, req_text: false, req_image: false, req_finding: false };
                  });
                }
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
      setZoomedIdInTable(`${newId.current}`);
    },
    [lang.types, ChecklistConfig, customWorkdayDurationFlag],
  );

  const onNameInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.name = value;
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDescriptionInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.description = value;
              if (value === '') {
                delete changedQuestion.description;
              }
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onStaticTextInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.static_text = value;
              if (value === '') {
                delete changedQuestion.static_text;
              }
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDateModeInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.dateMode = value;
              if (value === 'date') changedQuestion.dateFormat = 'DD-MM-YYYY';
              if (value === 'datetime') changedQuestion.dateFormat = 'DD-MM-YYYY HH:mm';
              if (value === 'time') changedQuestion.dateFormat = 'HH:mm';
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onRequiredInTableChange = useCallback((id: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.req = changedQuestion.req ? undefined : '1';
              if (changedQuestion.req === undefined) {
                delete changedQuestion.req;
              }
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          if (newTableQuestions.some((q) => q.req === '1')) {
            newQuestion.req = '1';
          } else {
            delete newQuestion.req;
          }
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDisableGalleryInTableChange = useCallback((id: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.disableGallery = changedQuestion.disableGallery ? undefined : '1';
              if (changedQuestion.disableGallery === undefined) {
                delete changedQuestion.disableGallery;
              }
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onMultiPhotoInTableChange = useCallback((id: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.type =
                changedQuestion.type === 'multi_photo' ? 'photo' : 'multi_photo';
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onBarcodeTypeInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.barcodeType = value;
              if (value === '0') {
                delete changedQuestion.barcodeType;
              }
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onOptionInTableChange = useCallback(
    (id: string, index: number, action: string, value: string, tableId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = {
                  ...q,
                  options: [...(q.options ? q.options : [])],
                };
                switch (action) {
                  case 'create':
                    if (changedQuestion.type === 'select_option_metadata_comments') {
                      changedQuestion.options.push({
                        option: value,
                        req_text: false,
                        req_image: false,
                        req_finding: false,
                      });
                    } else changedQuestion.options.push(value);
                    break;
                  case 'update':
                    if (changedQuestion.type === 'select_option_metadata_comments') {
                      changedQuestion.options[index].option = value;
                    } else changedQuestion.options[index] = value;
                    break;
                  case 'delete':
                    changedQuestion.options.splice(index, 1);
                }
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },

    [],
  );

  const onMetadataCodeInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      const questionIdsToDeleteAssociatedMetadata: string[] = [];
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              delete changedQuestion.nestableLocations;
              delete changedQuestion.selectedAssets;
              if (value === 'none') {
                delete changedQuestion.metadata;
                changedQuestion.listChoices = [];
              } else {
                if (changedQuestion.metadata === 'assets') {
                  const configs =
                    (changedQuestion as MetadataOptionsQuestion).meterConfigurations ?? [];
                  configs.forEach((cfg: MeterConfiguration) =>
                    questionIdsToDeleteAssociatedMetadata.push(...(cfg.associatedQuestions ?? [])),
                  );
                  delete changedQuestion.meterConfigurations;
                  delete changedQuestion.meterId;
                  delete changedQuestion.updateData;
                  delete changedQuestion.associatedQuestions;
                }
                changedQuestion.metadata = value;
                delete changedQuestion.listChoices;
              }
              return changedQuestion;
            }
            return q;
          });
          if (questionIdsToDeleteAssociatedMetadata.length > 0) {
            newTableQuestions = newTableQuestions.map((q) => {
              if (!questionIdsToDeleteAssociatedMetadata.includes(q.id)) return q;
              const updatedQuestion = { ...q };
              delete updatedQuestion.associatedMetadata;
              return updatedQuestion;
            });
          }
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onListChoicesInTableChange = useCallback(
    (id: string, index: number, action: string, value: string, tableId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = {
                  ...q,
                  listChoices: [...(q.listChoices ? q.listChoices : [])],
                };
                if (action === 'create') changedQuestion.listChoices.push(value);
                if (action === 'update') changedQuestion.listChoices[index] = value;
                if (action === 'delete') changedQuestion.listChoices.splice(index, 1);
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const reorderInTable = useCallback(
    (id: string, index: number, action: string, tableId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            const newTableQuestions = [...newQuestion.questions];

            const draggedQuestion = newTableQuestions.find((q) => q.id === id);
            newTableQuestions.splice(index, 1);
            newTableQuestions.splice(action === 'up' ? index - 1 : index + 1, 0, draggedQuestion);

            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const updateQuestions = (questions: any, index: number) => {
    let updated = false;
    const sectionId = questions[index].id;
    let currentIndex = index + 1;
    let currentQuestion;
    while (updated === false && questions[currentIndex]) {
      currentQuestion = questions[currentIndex];
      if (currentQuestion.type === 'section') updated = true;
      else {
        currentQuestion['sectionId'] = sectionId;
        if (currentQuestion.type === 'table') {
          currentQuestion.questions.forEach((question: any) => {
            question['sectionId'] = sectionId;
          });
          addSubformState(sectionId, currentIndex);
        }
        currentIndex += 1;
      }
    }
  };

  const addSectionState = (sectionId: string) => {
    setSectionStates((prevState: any) => {
      const newState = { ...prevState };
      newState[sectionId] = true;
      return newState;
    });
  };

  const addSectionHandler = useCallback(
    (index: number) => {
      let auxIndex = index;
      latestSectionId.current += 1;
      sectionCount.current += 1;
      setQuestions((prevQuestions) => {
        const newQuestions = [...prevQuestions];
        // adding the first section in the middle of a form creates another section at the top
        if (index !== 0 && newQuestions[0].type !== 'section') {
          newQuestions.splice(0, 0, {
            id: `s${latestSectionId.current}`,
            type: 'section',
            name: `${lang.form_builder.sectionName} ${latestSectionId.current}`,
            order: latestSectionId.current,
          });
          updateQuestions(newQuestions, 0);
          addSectionState(`s${latestSectionId.current}`);
          auxIndex += 1;
          latestSectionId.current += 1;
          sectionCount.current += 1;
        }
        newQuestions.splice(auxIndex, 0, {
          id: `s${latestSectionId.current}`,
          type: 'section',
          name: `${lang.form_builder.sectionName} ${latestSectionId.current}`,
          order: latestSectionId.current,
        });
        updateQuestions(newQuestions, auxIndex);
        addSectionState(`s${latestSectionId.current}`);
        return newQuestions;
      });
    },
    [lang.form_builder.sectionName],
  );

  const deleteSectionHandler = useCallback((id: string) => {
    sectionCount.current -= 1;
    setQuestions((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      const deletedSectionIndex = newQuestions.findIndex((question) => question.id === id);
      const previousSectionId = getQuestionSection(deletedSectionIndex, newQuestions);
      newQuestions.splice(deletedSectionIndex, 1);
      for (let i = deletedSectionIndex; true; i++) {
        if (newQuestions[i] && newQuestions[i].type !== 'section')
          newQuestions[i].sectionId = previousSectionId;
        else break;
      }
      return newQuestions;
    });
  }, []);

  const deleteSecionAndQuestionsHandler = useCallback((id: string) => {
    if (sectionCount.current > 1) {
      sectionCount.current -= 1;
      setQuestions((prevQuestions) => {
        let deleteState = false;
        let newQuestions = [...prevQuestions];
        const filteredQuestions: any[] = [];
        newQuestions.forEach((q) => {
          if (!deleteState && q.type !== 'section') {
            filteredQuestions.push({ ...q });
          }
          if (q.id === id) deleteState = true;
          else if (q.type === 'section') {
            deleteState = false;
            filteredQuestions.push({ ...q });
          }
        });
        // Everything this cascade drops (the section marker plus every question it contained),
        // so a surviving top-level question's relative-range reference into any of them can be
        // swept the same way a single deletion is.
        const keptIds = new Set(filteredQuestions.map((q) => q.id));
        const deletedIds = new Set(newQuestions.filter((q) => !keptIds.has(q.id)).map((q) => q.id));
        newQuestions = filteredQuestions.filter((question) => question.id !== id);
        return sweepDanglingDateQuestionReferences(newQuestions, deletedIds);
      });
    }
  }, []);

  const onNameInSectionChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.name = value;
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onSkipLogicChange = useCallback((id: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQ = { ...question };
          changedQ.skipLogic = changedQ.skipLogic ? undefined : '1';
          if (changedQ.skipLogic === undefined) {
            delete changedQ.skipLogic;
          } else {
            changedQ.skipLogicQuestion = changedQ.skipLogicQuestion || '';
            changedQ.skipLogicType = changedQ.skipLogicType || 'empty';
            changedQ.skipLogicValue = changedQ.skipLogicValue || '';
            changedQ.skipLogicTarget = changedQ.skipLogicTarget || 'next';
          }
          return changedQ;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDateValidationCheckboxChange = useCallback((id: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const updatedQuestion = { ...question };
          if (updatedQuestion.dateValidationType !== undefined) {
            removeDateQuestionValidationFields(updatedQuestion);
          } else {
            updatedQuestion.dateValidationType = '';
          }
          return updatedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onDateValidationCheckboxTableChange = useCallback((id: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId && question.type === 'table') {
          const updatedTableQuestion = { ...question };
          updatedTableQuestion.questions = updatedTableQuestion.questions.map((q: any) => {
            if (q.id === id) {
              const updatedSubQuestion = { ...q };
              if (updatedSubQuestion.dateValidationType !== undefined) {
                removeDateQuestionValidationFields(updatedSubQuestion);
              } else {
                updatedSubQuestion.dateValidationType = '';
              }
              return updatedSubQuestion;
            }
            return q;
          });
          return updatedTableQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onSkipSettingChange = useCallback((id: string, set: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      const skipLogicQuestionType = newQuestions.find(
        (q: QuestionInterface) => q.id === value,
      )?.type;
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          switch (set) {
            case 'question':
              changedQuestion.skipLogicQuestion = value;
              // If we are changing to a question with complex data (see disableOption function), the skip logic type must be either empty or not empty.
              if (
                disableOption(skipLogicQuestionType) &&
                ['equal', 'dif', 'contains', 'not_contains'].includes(changedQuestion.skipLogicType)
              )
                changedQuestion.skipLogicType = 'empty';
              break;
            case 'type': {
              const prevType = changedQuestion.skipLogicType;
              changedQuestion.skipLogicType = value;
              if (['empty', 'not_empty'].includes(value)) {
                changedQuestion.skipLogicValue = '';
              } else if (isEqualGroup(prevType) !== isEqualGroup(value)) {
                const sourceQuestion = newQuestions.find(
                  (q: QuestionInterface) => q.id === changedQuestion.skipLogicQuestion,
                );
                if (sourceUsesNestableLocations(sourceQuestion?.nestableLocations)) {
                  changedQuestion.skipLogicValue = '';
                }
              }
              break;
            }
            case 'target':
              changedQuestion.skipLogicTarget = value;
              break;
            case 'value':
              changedQuestion.skipLogicValue = value;
              break;
            default:
              console.log(set, value);
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onSkipLogicInTableChange = useCallback((id: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              if (q.skipLogic === '1') {
                delete q.skipLogic;
              } else {
                q.skipLogic = '1';
                q.skipLogicQuestion = q.skipLogicQuestion || '';
                q.skipLogicType = q.skipLogicType || 'empty';
                q.skipLogicValue = q.skipLogicValue || '';
                q.skipLogicTarget = q.skipLogicTarget || 'next';
              }
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onSkipSettingInTableChange = useCallback(
    (id: string, set: string, value: string, tableId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const skipLogicQuestionType = newQuestions.find(
                  (q: QuestionInterface) => q.id === value,
                )?.type;
                switch (set) {
                  case 'question':
                    q.skipLogicQuestion = value;
                    // If we are changing to a question with complex data (see disableOption function), the skip logic type must be either empty or not empty.
                    if (
                      disableOption(skipLogicQuestionType) &&
                      ['equal', 'dif', 'contains', 'not_contains'].includes(q.skipLogicType)
                    )
                      q.skipLogicType = 'empty';
                    break;
                  case 'type': {
                    const prevType = q.skipLogicType;
                    q.skipLogicType = value;
                    if (['empty', 'not_empty'].includes(value)) {
                      q.skipLogicValue = '';
                    } else if (isEqualGroup(prevType) !== isEqualGroup(value)) {
                      const sourceQuestion = newTableQuestions.find(
                        (tq: QuestionInterface) => tq.id === q.skipLogicQuestion,
                      );
                      if (sourceUsesNestableLocations(sourceQuestion?.nestableLocations)) {
                        q.skipLogicValue = '';
                      }
                    }
                    break;
                  }
                  case 'value':
                    q.skipLogicValue = value;
                    break;
                  case 'target':
                    q.skipLogicTarget = value;
                    break;
                  default:
                    console.log(set, value);
                }
                if (q.skipLogicTable !== tableId) q.skipLogicTable = tableId;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const numberMetadataSetting = useCallback((id: string, tag: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion[tag] = changedQuestion[tag] ? undefined : '1';
          if (!changedQuestion[tag]) delete changedQuestion[tag];
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const numberMetadataInTableSetting = useCallback((id: string, tag: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion[tag] = changedQuestion[tag] ? undefined : '1';
              if (!changedQuestion[tag]) delete changedQuestion[tag];
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const qualityCamOnChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          switch (value) {
            case '1':
              changedQuestion.cameraQuality = '1';
              changedQuestion.maxHeight = '1000';
              changedQuestion.maxWidth = '1000';
              break;
            case '0.8':
              changedQuestion.cameraQuality = '0.8';
              changedQuestion.maxHeight = '800';
              changedQuestion.maxWidth = '800';
              break;
            case '0.6':
              changedQuestion.cameraQuality = '0.6';
              changedQuestion.maxHeight = '600';
              changedQuestion.maxWidth = '600';
              break;
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const qualityCamInTableChange = useCallback(
    (id: string, value: string, tableId: string | number) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = { ...q };
                switch (value) {
                  case '1':
                    changedQuestion.cameraQuality = '1';
                    changedQuestion.maxHeight = '1000';
                    changedQuestion.maxWidth = '1000';
                    break;
                  case '0.8':
                    changedQuestion.cameraQuality = '0.8';
                    changedQuestion.maxHeight = '800';
                    changedQuestion.maxWidth = '800';
                    break;
                  case '0.6':
                    changedQuestion.cameraQuality = '0.6';
                    changedQuestion.maxHeight = '600';
                    changedQuestion.maxWidth = '600';
                    break;
                }
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const repeatedSections = () => {
    const sectionsNames: Record<string, number> = {};
    questions
      .filter((question) => question.type === 'section')
      .forEach((section) => {
        if (sectionsNames[section.name]) sectionsNames[section.name]++;
        else sectionsNames[section.name] = 1;
      });

    return Object.keys(sectionsNames)
      .filter((name) => sectionsNames[name] > 1)
      .join(', ');
  };

  const isSectionNameRepeated = () => {
    const names: any[] = [];
    let repeated = false;
    questions
      .filter((q) => q.type === 'section' && q.name !== '')
      .forEach((s) => {
        if (!names.includes(s.name)) names.push(s.name);
        else repeated = s.name;
      });
    return repeated;
  };

  const onFileSettingChange = useCallback(
    (id: string, value: number | string[], action: string) => {
      setQuestions((prevQuestion) => {
        let newQuestions = [...prevQuestion];
        newQuestions = newQuestions.map((question) => {
          if (question.id === id) {
            const changedQuestion = {
              ...question,
              formats: [...(question.formats ? question.formats : [])],
            };
            if (action === 'formats') changedQuestion.formats = value;
            if (action === 'quantity') changedQuestion.quantity = value;
            if (action === 'maximumAudioDurationInSeconds') {
              changedQuestion.maximumAudioDurationInSeconds = value;
            }
            return changedQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onFileSettingInTableChange = useCallback(
    (id: string, value: number | string[], action: string, tableId: string | number) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = {
                  ...q,
                  formats: [...(q.formats ? q.formats : [])],
                };
                if (action === 'formats') changedQuestion.formats = value;
                if (action === 'quantity') changedQuestion.quantity = value;
                if (action === 'maximumAudioDurationInSeconds') {
                  changedQuestion.maximumAudioDurationInSeconds = value;
                }
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onActivityPeriodTimeSettingChange = useCallback(
    (id: string, value: string, action: string, tableId: string | number) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === id) {
            const changedQuestion = { ...question };
            changedQuestion[action] = value;
            return changedQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onActivityPeriodTimeSettingInTableChange = useCallback(
    (id: string, value: string, action: string, tableId: string | number) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = { ...q };
                changedQuestion[action] = value;
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onRelatedListChange = useCallback((id: string, action: string, value: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          if (action === 'separator') {
            changedQuestion.separator = value;
            return changedQuestion;
          } else if (action === 'list_header') {
            changedQuestion.list_header = value;
            return changedQuestion;
          }
          if (changedQuestion.related_list === undefined) {
            changedQuestion.related_list = '1';
            changedQuestion.separator = '|';
            changedQuestion.list_header = lang.form_builder.headerPlaceholder;
          } else {
            delete changedQuestion.related_list;
            delete changedQuestion.separator;
            delete changedQuestion.list_header;
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onRelatedListInTableChange = useCallback(
    (id: string, action: string, value: string, tableId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((q) => {
              if (q.id === id) {
                const changedQuestion = { ...q };
                if (action === 'separator') {
                  changedQuestion.separator = value;
                  return changedQuestion;
                } else if (action === 'list_header') {
                  changedQuestion.list_header = value;
                  return changedQuestion;
                }
                if (changedQuestion.related_list === undefined) {
                  changedQuestion.related_list = '1';
                  changedQuestion.separator = '|';
                  changedQuestion.list_header = lang.form_builder.headerPlaceholder;
                } else {
                  delete changedQuestion.related_list;
                  delete changedQuestion.separator;
                  delete changedQuestion.list_header;
                }
                return changedQuestion;
              }
              return q;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onNestedLocationsAllSelected = useCallback((id: string, allLocationsSelected: boolean) => {
    const changeQuestion = (question: QuestionUnsafe) => {
      const changedQuestion = { ...question };
      changedQuestion.metadata = '';
      delete changedQuestion.listChoices;
      changedQuestion.nestableLocations = {
        selectedLocations: {},
        all_locations_selected: allLocationsSelected,
      };
      return changedQuestion;
    };

    setQuestions((prevQuestions) => {
      let newQuestions: QuestionUnsafe[] = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.type === 'table') {
          let newTableQuestions = [...question.questions];
          newTableQuestions = newTableQuestions.map((subQuestion) => {
            if (subQuestion.id === id) {
              return changeQuestion(subQuestion);
            }
            return subQuestion;
          });
          question.questions = newTableQuestions;
          return question;
        }

        if (question.id === id) {
          return changeQuestion(question);
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onNestedLocationsChange = (id: string, selectedLocations: Map<number, SelectedStatus>) => {
    setQuestions((prevQuestions) => {
      let newQuestions: QuestionUnsafe[] = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          changedQuestion.metadata = '';
          delete changedQuestion.listChoices;

          changedQuestion.nestableLocations = {
            selectedLocations: convertSelectedLocationsToObject(selectedLocations),
          };
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  };

  const onNestedLocationsInTableChange = (
    id: string,
    selectedLocations: Map<number, SelectedStatus>,
    tableId: string,
  ) => {
    setQuestions((prevQuestions) => {
      let newQuestions: QuestionUnsafe[] = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.metadata = '';
              changedQuestion.nestableLocations = {
                selectedLocations: convertSelectedLocationsToObject(selectedLocations),
              };
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  };

  const onSelectedAssetsChange = (
    id: string,
    selectedAssets: { assets: number[]; asset_types: number[] },
  ) => {
    setQuestions((prevQuestions) => {
      const questionIdsToDeleteAssociatedMetadata: string[] = [];
      let newQuestions = prevQuestions.map((question) => {
        if (question.id !== id) return question;
        const changedQuestion = { ...question };
        changedQuestion.metadata = 'assets';
        delete changedQuestion.nestableLocations;
        delete changedQuestion.listChoices;
        changedQuestion.selectedAssets = selectedAssets;
        const oldConfigs = normalizeMeterConfigurations(changedQuestion as MetadataOptionsQuestion);
        oldConfigs.forEach((cfg) =>
          questionIdsToDeleteAssociatedMetadata.push(...(cfg.associatedQuestions ?? [])),
        );
        delete changedQuestion.meterConfigurations;
        delete changedQuestion.meterId;
        delete changedQuestion.associatedQuestions;
        delete changedQuestion.updateData;
        return changedQuestion;
      });
      if (questionIdsToDeleteAssociatedMetadata.length > 0) {
        newQuestions = newQuestions.map((question) => {
          if (!questionIdsToDeleteAssociatedMetadata.includes(question.id)) return question;
          const updatedQuestion = { ...question };
          delete updatedQuestion.associatedMetadata;
          return updatedQuestion;
        });
      }
      return newQuestions;
    });
  };

  // "Filter assets by location" (DS-3776): links an asset question to a
  // nestable_locations question of the form (or clears the link when null).
  const onFilterByChange = (
    id: string,
    filterBy: { questionId: string; questionType: string } | null,
  ) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((question) => {
        if (question.id !== id) return question;
        const changedQuestion = { ...question };
        if (filterBy) {
          changedQuestion.filterBy = filterBy;
        } else {
          delete changedQuestion.filterBy;
        }
        return changedQuestion;
      }),
    );
  };

  // "Filter assets by location" (DS-3776): creates a Locations question right
  // after the asset question and links the filter to it.
  //
  // Without this the dropdown is a dead end in the state every new form starts
  // in: no location question yet, so nothing to pick. The question is created
  // already usable (`all_locations_selected`) rather than as a bare data-list
  // question, so it shows up in the dropdown immediately and can be linked in the
  // same action; narrowing the location scope afterwards is one click away.
  const onCreateLocationFilterQuestion = (assetQuestionId: string) => {
    newId.current += 1;
    const newQuestionId = `${newId.current}`;
    let insertedIdx: number | null = null;

    setQuestions((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      const parentIdx = newQuestions.findIndex((question) => question.id === assetQuestionId);
      const insertAt = parentIdx >= 0 ? parentIdx + 1 : newQuestions.length;
      const newQuestion: QuestionUnsafe = {
        id: newQuestionId,
        name: lang.form_builder.filterAssetsByLocationNewQuestionName,
        type: 'select_metadata',
        metadata: '',
        nestableLocations: { selectedLocations: {}, all_locations_selected: true },
        sectionId: sectionCount.current > 0 ? getQuestionSection(insertAt, newQuestions) : null,
      } as QuestionUnsafe;
      newQuestions.splice(insertAt, 0, newQuestion);
      insertedIdx = insertAt;

      // Link it in the same pass, so the two states the builder can be left in
      // (question created / filter configured) never diverge.
      return newQuestions.map((question) =>
        question.id === assetQuestionId
          ? {
              ...question,
              filterBy: { questionId: newQuestionId, questionType: 'select_metadata' },
            }
          : question,
      );
    });

    setZoomedId(newQuestionId);
    if (insertedIdx !== null) setScrolledIdx(insertedIdx);
  };

  // Table variants of the two handlers above (DS-3592). An asset question inside a
  // repeatable group may link to a sibling of its own row or to a top-level
  // location question; both live in `filterBy` the same way, so only the mutation
  // path differs.
  const onFilterByInTableChange = (
    id: string,
    filterBy: { questionId: string; questionType: string } | null,
    tableId: string,
  ) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((question) => {
        if (question.id !== tableId) return question;
        const newQuestion = { ...question };
        newQuestion.questions = (newQuestion.questions ?? []).map((q: QuestionUnsafe) => {
          if (q.id !== id) return q;
          const changedQuestion = { ...q };
          if (filterBy) {
            changedQuestion.filterBy = filterBy;
          } else {
            delete changedQuestion.filterBy;
          }
          return changedQuestion;
        });
        return newQuestion;
      }),
    );
  };

  // Creates the location question as a **sibling in the same group**: the creator
  // is configuring a row, so a per-row location is the coherent default. A
  // form-level location is already offered in the dropdown whenever one exists.
  const onCreateLocationFilterQuestionInTable = (assetQuestionId: string, tableId: string) => {
    newId.current += 1;
    const newQuestionId = `${newId.current}`;

    setQuestions((prevQuestions) =>
      prevQuestions.map((question) => {
        if (question.id !== tableId) return question;
        const newQuestion = { ...question };
        const siblings = [...(newQuestion.questions ?? [])];
        const parentIdx = siblings.findIndex((q: QuestionUnsafe) => q.id === assetQuestionId);
        const insertAt = parentIdx >= 0 ? parentIdx + 1 : siblings.length;

        siblings.splice(insertAt, 0, {
          id: newQuestionId,
          name: lang.form_builder.filterAssetsByLocationNewQuestionName,
          type: 'select_metadata',
          metadata: '',
          nestableLocations: { selectedLocations: {}, all_locations_selected: true },
          sectionId: newQuestion.sectionId ?? null,
        } as QuestionUnsafe);

        // Link it in the same pass, so the group is never left with the question
        // created but the filter unconfigured.
        newQuestion.questions = siblings.map((q: QuestionUnsafe) =>
          q.id === assetQuestionId
            ? {
                ...q,
                filterBy: { questionId: newQuestionId, questionType: 'select_metadata' },
              }
            : q,
        );
        return newQuestion;
      }),
    );

    setZoomedIdInTable(newQuestionId);
  };

  const onSelectedAssetsInTableChange = (
    id: string,
    selectedAssets: { assets: number[]; asset_types: number[] },
    tableId: string,
  ) => {
    setQuestions((prevQuestions) => {
      return prevQuestions.map((question) => {
        if (question.id !== tableId) return question;
        const newQuestion = { ...question };
        const questionIdsToDeleteAssociatedMetadata: string[] = [];
        newQuestion.questions = (newQuestion.questions ?? []).map((q: QuestionUnsafe) => {
          if (q.id !== id) return q;
          const changedQuestion = { ...q };
          changedQuestion.metadata = 'assets';
          delete changedQuestion.nestableLocations;
          changedQuestion.selectedAssets = selectedAssets;
          const oldConfigs = normalizeMeterConfigurations(
            changedQuestion as MetadataOptionsQuestion,
          );
          oldConfigs.forEach((cfg) =>
            questionIdsToDeleteAssociatedMetadata.push(...(cfg.associatedQuestions ?? [])),
          );
          delete (changedQuestion as MetadataOptionsQuestion).meterConfigurations;
          delete (changedQuestion as MetadataOptionsQuestion & { meterId?: string }).meterId;
          delete (changedQuestion as MetadataOptionsQuestion & { associatedQuestions?: string[] })
            .associatedQuestions;
          delete (changedQuestion as MetadataOptionsQuestion & { updateData?: string }).updateData;
          return changedQuestion;
        });
        if (questionIdsToDeleteAssociatedMetadata.length > 0) {
          newQuestion.questions = (newQuestion.questions ?? []).map((q: QuestionUnsafe) => {
            if (!questionIdsToDeleteAssociatedMetadata.includes(q.id)) return q;
            const updatedQuestion = { ...q };
            delete updatedQuestion.associatedMetadata;
            return updatedQuestion;
          });
        }
        return newQuestion;
      });
    });
  };

  const onRichTextChange = useCallback((id: string, value: string) => {
    setQuestions((prevQuestion) => {
      let newQuestions = [...prevQuestion];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = {
            ...question,
          };
          changedQuestion.rich_text = value;
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onRichTextInTableChange = useCallback((id: string, value: string, tableId: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === tableId) {
          const newQuestion = { ...question };
          let newTableQuestions = [...newQuestion.questions];
          newTableQuestions = newTableQuestions.map((q) => {
            if (q.id === id) {
              const changedQuestion = { ...q };
              changedQuestion.rich_text = value;
              return changedQuestion;
            }
            return q;
          });
          newQuestion.questions = newTableQuestions;
          return newQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onNumberValidationChange = useCallback((id: string, action: string, value: string) => {
    setQuestions((prevQuestions) =>
      applyNumberValidationChangeToQuestions(prevQuestions, id, action, value),
    );
  }, []);

  const onNumberValidationTableChange = useCallback(
    (id: string, action: string, value: string, tableId: string) => {
      setQuestions((prevQuestions) =>
        applyNumberValidationChangeInTable(prevQuestions, id, action, value, tableId),
      );
    },
    [],
  );

  const onDateValidationChange = useCallback(
    (id: string, dateQuestionFieldToUpdate: DateQuestionField, value: string) => {
      setQuestions((prevQuestion) => {
        let newQuestions = [...prevQuestion];
        newQuestions = newQuestions.map((question) => {
          if (question.id === id) {
            const changedQuestion = {
              ...question,
            };
            changedQuestion[dateQuestionFieldToUpdate] = value;
            if (dateQuestionFieldToUpdate === 'dateValidationType') {
              if (value === '') {
                removeDateQuestionValidationFields(changedQuestion);
              } else {
                const previousValidationType = question.dateValidationType;
                if (previousValidationType && previousValidationType !== value) {
                  removeDateQuestionValidationOffsetFields(changedQuestion);
                  delete changedQuestion.rangeCondition;
                }
              }
            }
            if (dateQuestionFieldToUpdate === 'rangeCondition') {
              if (value === '') {
                removeDateQuestionValidationOffsetFields(changedQuestion);
                delete changedQuestion.rangeCondition;
              } else if (takesNoOffset(value as RangeConditionType)) {
                removeDateQuestionValidationOffsetFields(changedQuestion);
              } else {
                const dateMode = (changedQuestion.dateMode as DateQuestionModeType) || 'datetime';
                const defaultOffsetInHours = getOffsetBounds(
                  dateMode,
                  changedQuestion.dateValidationType,
                ).min.toString();
                if (takesTwoOffsets(value as RangeConditionType)) {
                  delete changedQuestion.offsetHours;
                  changedQuestion.offsetHoursToLeft =
                    changedQuestion.offsetHoursToLeft || defaultOffsetInHours;
                  changedQuestion.offsetHoursToRight =
                    changedQuestion.offsetHoursToRight || defaultOffsetInHours;
                } else {
                  delete changedQuestion.offsetHoursToLeft;
                  delete changedQuestion.offsetHoursToRight;
                  changedQuestion.offsetHours = changedQuestion.offsetHours || defaultOffsetInHours;
                }
              }
            }
            return changedQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onDateValidationBatchChange = useCallback(
    (
      questionId: string,
      dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    ) => {
      setQuestions((prevQuestion) => {
        let newQuestions = [...prevQuestion];
        newQuestions = newQuestions.map((question) => {
          if (question.id === questionId) {
            const changedQuestion = {
              ...question,
            };
            Object.entries(dateQuestionFieldsToUpdate).forEach(([field, value]) => {
              if (value === '') {
                delete changedQuestion[field as DateQuestionField];
              } else {
                changedQuestion[field as DateQuestionField] = value;
              }
            });
            return changedQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onDateValidationTableBatchChange = useCallback(
    (
      questionIdInTable: string,
      dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
      tableId: string,
    ) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((tableQuestion) => {
              if (tableQuestion.id === questionIdInTable) {
                const changedQuestion = { ...tableQuestion };
                Object.entries(dateQuestionFieldsToUpdate).forEach(([field, value]) => {
                  if (value === '') {
                    delete changedQuestion[field as DateQuestionField];
                  } else {
                    changedQuestion[field as DateQuestionField] = value;
                  }
                });
                return changedQuestion;
              }
              return tableQuestion;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onDateValidationTableChange = useCallback(
    (id: string, dateQuestionFieldToUpdate: DateQuestionField, value: string, tableId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === tableId) {
            const newQuestion = { ...question };
            let newTableQuestions = [...newQuestion.questions];
            newTableQuestions = newTableQuestions.map((tableQuestion) => {
              if (tableQuestion.id === id) {
                const changedQuestion = { ...tableQuestion };
                changedQuestion[dateQuestionFieldToUpdate] = value;
                if (dateQuestionFieldToUpdate === 'dateValidationType') {
                  if (value === '') {
                    removeDateQuestionValidationFields(changedQuestion);
                  } else {
                    const previousValidationType = tableQuestion.dateValidationType;
                    if (previousValidationType && previousValidationType !== value) {
                      removeDateQuestionValidationOffsetFields(changedQuestion);
                      delete changedQuestion.rangeCondition;
                    }
                  }
                }
                if (dateQuestionFieldToUpdate === 'rangeCondition') {
                  if (value === '') {
                    removeDateQuestionValidationOffsetFields(changedQuestion);
                    delete changedQuestion.rangeCondition;
                  } else if (takesNoOffset(value as RangeConditionType)) {
                    removeDateQuestionValidationOffsetFields(changedQuestion);
                  } else {
                    const dateMode =
                      (changedQuestion.dateMode as DateQuestionModeType) || 'datetime';
                    const defaultOffsetInHours = getOffsetBounds(
                      dateMode,
                      changedQuestion.dateValidationType,
                    ).min.toString();
                    if (takesTwoOffsets(value as RangeConditionType)) {
                      delete changedQuestion.offsetHours;
                      changedQuestion.offsetHoursToLeft =
                        changedQuestion.offsetHoursToLeft || defaultOffsetInHours;
                      changedQuestion.offsetHoursToRight =
                        changedQuestion.offsetHoursToRight || defaultOffsetInHours;
                    } else {
                      delete changedQuestion.offsetHoursToLeft;
                      delete changedQuestion.offsetHoursToRight;
                      changedQuestion.offsetHours =
                        changedQuestion.offsetHours || defaultOffsetInHours;
                    }
                  }
                }
                return changedQuestion;
              }
              return tableQuestion;
            });
            newQuestion.questions = newTableQuestions;
            return newQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onOptionsWithCommentsChange = useCallback((id: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          // grabs modified question
          const changedQuestion = { ...question };
          if (changedQuestion.type === 'select_option_metadata') {
            // if it's a select_option_metadata question, change it to select_option_metadata_comments and change its options data structure
            changedQuestion.type = 'select_option_metadata_comments';
            changedQuestion.options = changedQuestion.options.map((option: string) => {
              return { option: option, req_text: false, req_image: false, req_finding: false };
            });
          } else {
            // if it's a select_option_metadata_comments question, change it to select_option_metadata and change its options data structure
            changedQuestion.type = 'select_option_metadata';
            changedQuestion.options = changedQuestion.options.map((option: any) => {
              return option.option;
            });
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onObligatoryOptionCommentChange = useCallback(
    (id: string, optionIndex: number, reqText: boolean, reqImage: boolean, reqFinding: boolean) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((question) => {
          if (question.id === id) {
            // grabs modified question
            const changedQuestion = cloneDeep(question);
            // Ensure the option at the given index is an object
            const currentOption = changedQuestion.options[optionIndex];
            if (
              typeof currentOption === 'object' &&
              currentOption !== null &&
              !(currentOption instanceof String)
            ) {
              // Update req_text and req_image only if it's an object
              currentOption.req_text = reqText ?? !currentOption.req_text; // flips required text comment for option selected
              currentOption.req_image = reqImage ?? !currentOption.req_image; // flips required image comment for option selected
              currentOption.req_finding = reqFinding ?? !currentOption.req_finding; // flips required finding comment for option selected
            }
            return changedQuestion;
          }
          return question;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onOptionsWithCommentsInTableChange = useCallback(
    // same as onOptionsWithCommentsChange but for table questions
    (tableId: string, questionId: string) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((table) => {
          if (table.id === tableId) {
            const newTableQuestion = { ...table };
            let changedTableQuestions = [...newTableQuestion.questions];
            changedTableQuestions = changedTableQuestions.map((question) => {
              if (question.id === questionId) {
                const changedQuestion = { ...question };
                if (changedQuestion.type === 'select_option_metadata') {
                  changedQuestion.type = 'select_option_metadata_comments';
                  changedQuestion.options = changedQuestion.options.map((option: string) => {
                    return {
                      option: option,
                      req_text: false,
                      req_image: false,
                      req_finding: false,
                    };
                  });
                } else {
                  changedQuestion.type = 'select_option_metadata';
                  changedQuestion.options = changedQuestion.options.map((option: any) => {
                    return option.option;
                  });
                }
                return changedQuestion;
              }
              return question;
            });
            newTableQuestion.questions = changedTableQuestions;
            return newTableQuestion;
          }
          return table;
        });
        return newQuestions;
      });
    },
    [],
  );

  const onObligatoryOptionCommentInTableChange = useCallback(
    // same as onObligatoryOptionCommentChange but for table questions
    (
      tableId: string,
      questionId: string,
      optionIndex: number,
      reqText: boolean,
      reqImage: boolean,
      reqFinding: boolean,
    ) => {
      setQuestions((prevQuestions) => {
        let newQuestions = [...prevQuestions];
        newQuestions = newQuestions.map((table) => {
          if (table.id === tableId) {
            const newTableQuestion = cloneDeep(table);
            let changedTableQuestions = [...newTableQuestion.questions];
            changedTableQuestions = changedTableQuestions.map((question) => {
              if (question.id === questionId) {
                const changedQuestion = cloneDeep(question);
                // Ensure the option at the given index is an object
                const currentOption = changedQuestion.options[optionIndex];
                if (
                  typeof currentOption === 'object' &&
                  currentOption !== null &&
                  !(currentOption instanceof String)
                ) {
                  // Update req_text and req_image only if it's an object
                  currentOption.req_text = reqText ?? !currentOption.req_text; // flips required text comment for option selected
                  currentOption.req_image = reqImage ?? !currentOption.req_image; // flips required image comment for option selected
                  currentOption.req_finding = reqFinding || false; // flips required finding comment for option selected
                }
                return changedQuestion;
              }
              return question;
            });
            newTableQuestion.questions = changedTableQuestions;
            return newTableQuestion;
          }
          return table;
        });
        return newQuestions;
      });
    },
    [],
  );

  const dataGetRequest = async (refresh: any) => {
    if (formId !== 'new') dispatch(updateCurrentTaskFormId(parseInt(formId)));
    try {
      setLoading(true);
      const response = await client.get(`/task_forms_get/${formId}`);
      const { data } = response;
      setLinkToken(data.link_token);
      setNoFormUser(data.no_forms);
      setHelper(data.helper);
      setPaying(!!data.paying);
      setFormName(data.name);
      setFormUpdatesLockedAt(data.updates_locked_at);
      setOriginalFormName(data.name);
      setAvailableFileUpload(data.file_question);
      setListManagerFeature(data.list_Manager_allowed);
      dispatch(setIsActive(!!data.list_Manager_allowed));
      if (formId !== 'new') {
        // This is the logic for the edition logs of the form
        // We only set the initial data to track when the form is not new.
        // We cannot wrap this in a flag, because it runs on the first render, and therefore the flag is not available
        setInitialDataToTrack(data.questions, data.name, formId);

        if (!refresh) setZoomedId(data.questions[0].id);
        setQuestions(() => {
          let greaterId = 0;
          let sectId = 0;
          let sectCount = 0;
          let label = '';
          const newQuestions = [...data.questions];
          data.questions.forEach((question: any, index: number) => {
            if (parseInt(question.id, 10) > greaterId) {
              greaterId = parseInt(question.id, 10);
            }
            if (question.type === 'table') {
              question.questions.forEach((q: any) => {
                if (parseInt(q.id, 10) > greaterId) {
                  greaterId = parseInt(q.id, 10);
                }
                // Add skip logic conversion for table questions
                if (q.skipLogicType === 'equal' && q.skipLogicValue === '')
                  q.skipLogicType = 'empty';
                else if (q.skipLogicType === 'dif' && q.skipLogicValue === '')
                  q.skipLogicType = 'not_empty';
              });
            }
            if (question.label && question.label !== label) {
              sectId += 1;
              sectCount += 1;
              label = question.label;
              newQuestions.splice(index + sectId - 1, 0, {
                id: `s${sectId}`,
                type: 'section',
                name: label,
                order: sectId,
              });
              setSectionStates((prevStates) => {
                const newStates: any = { ...prevStates };
                newQuestions.forEach((q: any) => {
                  if (q.type === 'section') {
                    newStates[q.id] = true;
                  }
                });
                return newStates;
              });
            }
            if (question.type !== 'section' && sectId > 0) {
              question['sectionId'] = `s${sectId}`;
            }
            // Skip logic: If the condition is "equal to [empty]", set to "empty". If it is "different to [empty]", set to "not empty".
            if (question.skipLogicType === 'equal' && question.skipLogicValue === '')
              question.skipLogicType = 'empty';
            else if (question.skipLogicType === 'dif' && question.skipLogicValue === '')
              question.skipLogicType = 'not_empty';
          });
          sectionCount.current = sectCount;
          latestSectionId.current = sectId;
          newId.current = greaterId;
          return newQuestions;
        });
      } else {
        setQuestions(() => [
          {
            id: '1',
            name: `${lang.types.text} 1`,
            type: 'text',
          },
        ]);
      }
      if (!refresh) {
        const newData = [
          { tag: lang.form_builder.newListData, value: 'none' },
          ...data.sources.map((source: any) => ({
            tag: source[0],
            value: source[1],
          })),
        ];
        setDataSources(newData);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const buildQuestionsDict = () => {
    let label = '';
    const questionsWithLabels = questions
      .map((q) => {
        const labelQ = { ...q };
        if (labelQ.type === 'section') label = labelQ.name;
        else labelQ.label = label;
        return labelQ;
      })
      .filter((q) => q.type !== 'section');

    // Save-time backstop scope for cleanIncompleteDateValidationFields (belt-and-suspenders
    // alongside the delete-time sweep in onDeleteHandler/onDeleteInTableHandler/
    // deleteSecionAndQuestionsHandler): the ids a top-level question's relative-range reference
    // may legally point at, same top-level scope the builder's own reference picker offers.
    const topLevelReferenceIds = new Set(questionsWithLabels.map((q) => q.id));

    const questionsIntoDict = Object.assign(
      {},
      ...questionsWithLabels.map((question, index) => {
        const newQuestion: any = { ...question };
        // Captured before the array-to-dict rebuild below turns newQuestion.questions into an
        // index-keyed object: the ids a sub-question's own reference may point at, scoped to
        // this table only (a reference never crosses a table boundary).
        const tableReferenceIds: Set<string> | undefined =
          newQuestion.type === 'table' && Array.isArray(newQuestion.questions)
            ? new Set(newQuestion.questions.map((tableQuestion: any) => tableQuestion.id))
            : undefined;
        const defaultOptions = [
          `${lang.form_builder.alternative} 1`,
          `${lang.form_builder.alternative} 2`,
          `${lang.form_builder.alternative} 3`,
        ];
        if (
          ['multi_option', 'select_option_metadata', 'select_option_metadata_comments'].includes(
            newQuestion.type,
          )
        ) {
          if (newQuestion.options && newQuestion.options.length === 0) {
            newQuestion.options = [...defaultOptions];
          } else if (!newQuestion.options) {
            newQuestion.options = [...defaultOptions];
          }
        }
        if (newQuestion.type === 'table') {
          newQuestion.questions = Object.assign(
            {},
            ...newQuestion.questions.map((tableQuestion: any, tableIndex: any) => {
              const newInTableQ = { ...tableQuestion };
              if (
                [
                  'multi_option',
                  'select_option_metadata',
                  'select_option_metadata_comments',
                ].includes(newInTableQ.type)
              ) {
                if (newInTableQ.options && newInTableQ.options.length === 0) {
                  newInTableQ.options = [...defaultOptions];
                } else if (!newInTableQ.options) {
                  newInTableQ.options = [...defaultOptions];
                }
              }
              // Skip logic for table questions: convert empty to equal and not empty to different (for mobile)
              if (['empty', 'not_empty'].includes(newInTableQ.skipLogicType)) {
                newInTableQ.skipLogicType = newInTableQ.skipLogicType === 'empty' ? 'equal' : 'dif';
                newInTableQ.skipLogicValue = '';
              }
              return { [tableIndex]: newInTableQ };
            }),
          );
        }

        // Skip logic: convert empty to equal and not empty to different (for mobile)
        if (['empty', 'not_empty'].includes(newQuestion.skipLogicType)) {
          newQuestion.skipLogicType = newQuestion.skipLogicType === 'empty' ? 'equal' : 'dif';
          newQuestion.skipLogicValue = '';
        }

        // Date time validation: clean validation fields if validation is incomplete
        cleanIncompleteDateValidationFields(newQuestion, topLevelReferenceIds);

        // Date time validation for table questions: clean validation fields if validation is incomplete
        if (newQuestion.type === 'table' && newQuestion.questions) {
          Object.keys(newQuestion.questions).forEach((tableIndex) => {
            const tableQuestion = newQuestion.questions[tableIndex];
            cleanIncompleteDateValidationFields(tableQuestion, tableReferenceIds);
          });
        }

        return { [index]: newQuestion };
      }),
    );

    return questionsIntoDict;
  };

  const dataUpdateRequest = async () => {
    if (blockFormBuilder) return;
    setDisableButton(true);

    const questionsIntoDict = buildQuestionsDict();

    try {
      if (formBuilderLogs && formId !== 'new') {
        // If the update was successful, send the logs to the backend:
        const formBuilderLogData = await handleCompareAndLogData(
          Object.values(questionsIntoDict),
          formName,
        ); // We need an array of Questions

        await client.post(
          '/task_forms_update',
          {
            form: {
              title: formName,
              questions: questionsIntoDict,
              id: formId,
            },
            log_change: formBuilderLogData.log_change,
          },
          {
            headers: {
              accept: 'application/json',
            },
          },
        );
      } else {
        const response = await client.post(
          '/task_forms_update',
          {
            form: {
              title: formName,
              questions: questionsIntoDict,
              id: formId,
            },
          },
          {
            headers: {
              accept: 'application/json',
            },
          },
        );
        if (formId === 'new') {
          datascopeTracker('Save new form', { source: 'FORM_BUILDER' }, 'AMP');
          const new_form_id = response.data.new_form_id;
          setFormId(new_form_id);
          navigate(`../${new_form_id}`, {
            replace: true,
            relative: 'path',
          });
        } else {
          await dataGetRequest(true);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDisableButton(false);
    }
  };

  const onSubformsLimitChange = useCallback((id: string, value: string, type: string) => {
    setQuestions((prevQuestions) => {
      let newQuestions = [...prevQuestions];
      newQuestions = newQuestions.map((question) => {
        if (question.id === id) {
          const changedQuestion = { ...question };
          if (type === 'min') {
            changedQuestion.min_subforms = value;
          } else if (type === 'max') {
            changedQuestion.max_subforms = value;
          }
          return changedQuestion;
        }
        return question;
      });
      return newQuestions;
    });
  }, []);

  const onSelectMetadata = useCallback(
    (id: string, value: string, tableId?: string) => {
      return tableId
        ? onMetadataCodeInTableChange(id, value, tableId)
        : onMetadataCodeChange(id, value);
    },
    [onMetadataCodeChange, onMetadataCodeInTableChange],
  );

  useEffect(() => {
    dataGetRequest(false);
    try {
      const formBuilderDivHeight = document.getElementById('mainFormBuilderDiv').clientHeight;
      setWindowHeight(formBuilderDivHeight);
    } catch (error) {
      setWindowHeight(600);
    }
  }, []);

  useEffect(() => {
    let count = 0;

    for (const i in questions) {
      if (questions[i].type !== 'section') {
        count += 1;
      }
      if (count === 2) break;
    }
    setDisableDelete(count < 2);
    const newLogicQuestions = questions
      .filter((item: SkipLogicQuestion) => item.type !== 'section')
      .map((q: SkipLogicQuestion) => ({
        id: q.id,
        name: q.name,
        type: q.type,
        dateMode: q.dateMode,
        nestableLocations: q.nestableLocations,
      }));
    setLogicQuestions(newLogicQuestions);
    const newTableLogicQuestions: { [key: string]: SkipLogicQuestion[] } = {};
    questions.forEach((question: { type: string; id: string; questions: SkipLogicQuestion[] }) => {
      if (question.type === 'table') {
        newTableLogicQuestions[question.id] = question.questions.map((q) => ({
          id: q.id,
          name: q.name,
          type: q.type,
          dateMode: q.dateMode,
          nestableLocations: q.nestableLocations,
        }));
      }
    });
    setTableLogicQuestions(newTableLogicQuestions);
  }, [questions]);

  useEffect(() => {
    const handler = (e: any) => setMatches(e.matches);
    try {
      mediaMatch.addEventListener('change', handler);
      return () => mediaMatch.removeEventListener('change', handler);
    } catch (e) {
      console.log(e);
    }
  });

  window.addEventListener('resize', () => {
    try {
      const h = document.getElementById('mainFormBuilderDiv').clientHeight;
      setWindowHeight(h);
    } catch (e) {
      setWindowHeight(600);
    }
  });

  const executeSave = async () => {
    // An activity question with no duration or no interval leaves the person in
    // the field with nothing to build their workday from, so it cannot be
    // persisted. The warning travels through two channels: the snackbar here,
    // because the offending question may be off screen, and the inline error on
    // the question itself, driven by invalidActivityQuestionIds.
    // This only applies with the flag on: the legacy selector cannot leave the
    // duration or the interval empty, so the block would have nothing to stop
    // there and could jam pre-existing forms.
    const invalidActivityQuestions = customWorkdayDurationFlag
      ? findInvalidActivityQuestions(questions)
      : [];
    // The offending ids are tracked one by one instead of with a single
    // form-wide flag, and the set is rewritten on every attempt. A question
    // added after a blocked save then starts clean rather than inheriting the
    // error state of the previous attempt.
    setInvalidActivityQuestionIds(new Set(invalidActivityQuestions.map((question) => question.id)));
    if (invalidActivityQuestions.length > 0) {
      enqueueSnackbar(lang.form_builder.activityPeriodTimeIncomplete, { variant: 'error' });
      // Rejects with the sentinel so the header does not stack its generic
      // error message on top of the specific one just shown.
      return Promise.reject(ACTIVITY_PERIOD_TIME_INCOMPLETE);
    }

    if (isSectionNameRepeated() === false) {
      await dataUpdateRequest();
    } else {
      setOpenedAlert(true);
      return Promise.reject();
    }
  };

  const handleClickSave = async () => {
    if (dynamicSignatureFeatureFlag && formId !== 'new') {
      try {
        const questionsDict = buildQuestionsDict();
        const hasModifications = await taskFormsApi.validateDynamicSignatureQuestionModification(
          Number(formId),
          { form: { questions: questionsDict } },
        );

        if (hasModifications) {
          setShowDynamicSignatureModifyingAlert(true);
          return;
        }
      } catch (error) {
        console.error('Error validating dynamic signature modifications:', error);
      }
    }
    await executeSave();
    enqueueSnackbar(lang.form_builder.snackBar, { variant: 'success' });
  };

  const handleConfirmAfterAlert = async () => {
    setShowDynamicSignatureModifyingAlert(false);
    // This is the second entry point into executeSave and, unlike the header
    // one, nothing upstream catches it: the dialog wires this straight to its
    // confirm button. Without the try/catch a blocked save (or the pre-existing
    // repeated-section rejection) becomes an unhandled promise rejection.
    // The sentinel is swallowed because executeSave already showed the specific
    // message; anything else falls back to the generic save error.
    try {
      await executeSave();
    } catch (error) {
      if (error !== ACTIVITY_PERIOD_TIME_INCOMPLETE) {
        enqueueSnackbar(lang.form_builder.saveError, { variant: 'error' });
      }
      return;
    }
    enqueueSnackbar(lang.form_builder.snackBar, { variant: 'success' });
  };

  if (loading) return <Loader />;

  return (
    <div
      id={'mainFormBuilderDiv'}
      className={'mainFormBuilderDiv'}
      style={styles.mainContainer}
    >
      <Header
        formName={formName}
        onNameChange={setFormName}
        formId={formId}
        originalFormName={originalFormName}
        helper={helper}
        disableButton={disableButton}
        onSave={handleClickSave}
        onShare={() => setOpenShareModal(true)}
      />
      {QRAllowed && (
        <Onboarding
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
          linkToken={linkToken}
          formId={formId}
        />
      )}
      <RepeatedSectionsAlert
        open={openedAlert}
        onClose={() => setOpenedAlert(false)}
        repeatedSections={repeatedSections()}
      />
      <ConfirmActionDialog
        isOpen={showDynamicSignatureModifyingAlert}
        warningTitle={lang.form_builder.dynamicSignatureModificationAlert.title}
        warningMessage={lang.form_builder.dynamicSignatureModificationAlert.description}
        onConfirmAction={handleConfirmAfterAlert}
        onClose={() => setShowDynamicSignatureModifyingAlert(false)}
        confirmActionButtonText={lang.form_builder.dynamicSignatureModificationAlert.continue}
        cancelActionButtonText={lang.form_builder.dynamicSignatureModificationAlert.cancel}
      />
      <div style={styles.bodyContainer}>
        {matches && (
          <div style={styles.leftContainer}>
            <QuestionNavigator
              questions={questions}
              zoomedId={zoomedId}
              hoveredId={hoveredId}
              onAddHandler={onAddHandler}
              onDragEndHandler={onDragEndHandler}
              onZoomedHandler={onZoomedHandler}
              onHoveredHandler={onHoveredHandler}
              onScrolledIdxHandler={onScrolledIdxHandler}
              windowHeight={windowHeight}
              onNameChange={onNameChange}
              onNameInSectionChange={onNameInSectionChange}
              sectionStates={sectionStates}
              setSectionStates={setSectionStates}
              combineAlert={combineAlert}
              setCombineAlert={setCombineAlert}
              combineAlertText={combineAlertText}
              subQuestionRemove={subQuestionRemove}
              subformEmpty={subformEmpty}
              emptySubformAlert={emptySubformAlert}
              setEmptySubformAlert={setEmptySubformAlert}
              onZoomedInTableHandler={setZoomedIdInTable}
              subformStates={subformStates}
              setSubformStates={setSubformStates}
              onNameInTableChange={onNameInTableChange}
            />
          </div>
        )}
        <div style={styles.middleContainer}>
          {blockFormBuilder && (
            <Box sx={customStyles.infoAlertContainer}>
              <InfoAlert
                message={
                  <Typography component='span'>
                    {lang.form_builder.formBuilderBlockedAlertPart1}
                    <Link
                      href={lang.pricing.salesLink}
                      underline='always'
                      target='_blank'
                    >
                      {lang.form_builder.formBuilderBlockedAlertPart2}
                    </Link>
                    {lang.form_builder.formBuilderBlockedAlertPart3}
                  </Typography>
                }
              />
            </Box>
          )}
          {questions.length > 0 ? (
            <QuestionList
              questions={questions}
              dataSources={dataSources}
              zoomedId={zoomedId}
              hoveredId={hoveredId}
              scrolledIdx={scrolledIdx}
              expanded={expanded}
              onDragEndHandler={onDragEndHandler}
              onZoomedHandler={onZoomedHandler}
              onHoveredHandler={onHoveredHandler}
              onExpandedHandler={onExpandedHandler}
              onAddHandler={onAddHandler}
              onDeleteHandler={onDeleteHandler}
              onDuplicateHandler={onDuplicateHandler}
              onTypeChange={onTypeChange}
              onNameChange={onNameChange}
              onDescriptionChange={onDescriptionChange}
              onStaticTextChange={onStaticTextChange}
              onDateModeChange={onDateModeChange}
              onRequiredChange={onRequiredChange}
              onTextValidationChange={onTextValidationChange}
              onNumberPercentageChange={onNumberPercentageChange}
              onBarcodeTypeChange={onBarcodeTypeChange}
              onDisableGalleryChange={onDisableGalleryChange}
              onMultiPhotoChange={onMultiPhotoChange}
              onOptionChange={onOptionChange}
              onMetadataCodeChange={onMetadataCodeChange}
              onFilterByChange={onFilterByChange}
              onCreateLocationFilterQuestion={onCreateLocationFilterQuestion}
              onListChoicesChange={onListChoicesChange}
              expandedInTable={expandedInTable}
              zoomedIdInTable={zoomedIdInTable}
              onZoomedInTableHandler={setZoomedIdInTable}
              onAddInTableHandler={onAddInTableHandler}
              onDeleteInTableHandler={onDeleteInTableHandler}
              onDuplicateInTableHandler={onDuplicateInTableHandler}
              onTypeInTableChange={onTypeInTableChange}
              onNameInTableChange={onNameInTableChange}
              onDescriptionInTableChange={onDescriptionInTableChange}
              onStaticTextInTableChange={onStaticTextInTableChange}
              onDateModeInTableChange={onDateModeInTableChange}
              onRequiredInTableChange={onRequiredInTableChange}
              onBarcodeTypeInTableChange={onBarcodeTypeInTableChange}
              onDisableGalleryInTableChange={onDisableGalleryInTableChange}
              onMultiPhotoInTableChange={onMultiPhotoInTableChange}
              onOptionInTableChange={onOptionInTableChange}
              onListChoicesInTableChange={onListChoicesInTableChange}
              reorderInTable={reorderInTable}
              windowHeight={windowHeight}
              disableDelete={disableDelete}
              onScrolledIdxHandler={onScrolledIdxHandler}
              addSectionHandler={addSectionHandler}
              onNameInSectionChange={onNameInSectionChange}
              duplicateSectionHandler={duplicateSectionHandler}
              deleteSectionHandler={deleteSectionHandler}
              deleteSecionAndQuestionsHandler={deleteSecionAndQuestionsHandler}
              sectionCount={sectionCount}
              onSkipLogicChange={onSkipLogicChange}
              onSkipSettingChange={onSkipSettingChange}
              onDateValidationCheckboxChange={onDateValidationCheckboxChange}
              onDateValidationCheckboxTableChange={onDateValidationCheckboxTableChange}
              onFilterByInTableChange={onFilterByInTableChange}
              onCreateLocationFilterQuestionInTable={onCreateLocationFilterQuestionInTable}
              logicQuestions={logicQuestions}
              paying={paying}
              numberMetadataSetting={numberMetadataSetting}
              numberMetadataInTableSetting={numberMetadataInTableSetting}
              qualityCamOnChange={qualityCamOnChange}
              qualityCamInTableChange={qualityCamInTableChange}
              onFileSettingChange={onFileSettingChange}
              onFileSettingInTableChange={onFileSettingInTableChange}
              onActivityPeriodTimeSettingChange={onActivityPeriodTimeSettingChange}
              onActivityPeriodTimeSettingInTableChange={onActivityPeriodTimeSettingInTableChange}
              invalidActivityQuestionIds={invalidActivityQuestionIds}
              availableFileUpload={availableFileUpload}
              onRelatedListChange={onRelatedListChange}
              onRelatedListInTableChange={onRelatedListInTableChange}
              onRichTextChange={onRichTextChange}
              onRichTextInTableChange={onRichTextInTableChange}
              onNumberValidationChange={onNumberValidationChange}
              onNumberValidationTableChange={onNumberValidationTableChange}
              onDateValidationChange={onDateValidationChange}
              onDateValidationTableChange={onDateValidationTableChange}
              onDateValidationBatchChange={onDateValidationBatchChange}
              onDateValidationTableBatchChange={onDateValidationTableBatchChange}
              onOptionsWithCommentsChange={onOptionsWithCommentsChange}
              onObligatoryOptionCommentChange={onObligatoryOptionCommentChange}
              onOptionsWithCommentsInTableChange={onOptionsWithCommentsInTableChange}
              onObligatoryOptionCommentInTableChange={onObligatoryOptionCommentInTableChange}
              onSubformsLimitChange={onSubformsLimitChange}
              onChecklistSettingsChange={onChecklistSettingsChange}
              onSkipLogicInTableChange={onSkipLogicInTableChange}
              onSkipSettingInTableChange={onSkipSettingInTableChange}
              onMeterChange={onMeterChange}
              onAddMeterConfiguration={onAddMeterConfiguration}
              onDeleteMeterConfiguration={onDeleteMeterConfiguration}
              onMeterConfigurationMeterChange={onMeterConfigurationMeterChange}
              onAddMeterConfigurationQuestion={onAddMeterConfigurationQuestion}
              onUpdateMeterConfigurationQuestion={onUpdateMeterConfigurationQuestion}
              onDeleteMeterConfigurationQuestion={onDeleteMeterConfigurationQuestion}
              onCreateAssetQuestion={onCreateAssetQuestion}
              tableLogicQuestions={tableLogicQuestions}
            />
          ) : (
            <div>
              <Skeleton
                variant='text'
                width={600}
                height={50}
              />
              <Skeleton
                variant='rectangular'
                width={600}
                height={170}
              />
            </div>
          )}
          {searchDataListModalFeature && searchDataListModalOpen && (
            <SearchDataListModal
              dataSources={dataSources}
              onSelect={onSelectMetadata}
              questions={questions}
              onNestedLocationsChange={onNestedLocationsChange}
              onNestedLocationsInTableChange={onNestedLocationsInTableChange}
              onNestedLocationsAllSelected={onNestedLocationsAllSelected}
              onSelectedAssetsChange={onSelectedAssetsChange}
              onSelectedAssetsInTableChange={onSelectedAssetsInTableChange}
            />
          )}
          {formId !== 'new' && (
            <ShareModal
              isOpen={openShareModal}
              onClosed={() => setOpenShareModal(false)}
            />
          )}
        </div>
        {matches && (
          <div style={styles.rightContainer}>{listManagerFeature && <ListInformation />}</div>
        )}
      </div>
    </div>
  );
};
